// Admin Event Day Manifest: track who jumped per round.
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  setDoc,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { firestore, functions } from '../firebase';
import { getDisplayName } from '../utils/flyerDisplay';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import { getFuryRegistrationIdToken } from '../utils/furyIdentityToken';
import './EventDayManifest.css';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const EDM_PLANE_ORDER_EXPORT_COLS = [
  { id: 'round', label: 'Round', default: true },
  { id: 'plane', label: 'Plane', default: true },
  { id: 'load', label: 'Load', default: true },
  { id: 'teamNumber', label: 'Team #', default: true },
  { id: 'mentor', label: 'Mentor', default: true },
  { id: 'freshlings', label: 'Freshlings', default: true },
];

const EDM_JUMP_COUNTS_EXPORT_COLS = [
  { id: 'name', label: 'Name', default: true },
  { id: 'email', label: 'Email', default: true },
  { id: 'jumps', label: 'Jumps', default: true },
];

const EDM_ROSTER_EXPORT_COLS = [
  { id: 'name', label: 'Name', default: true },
  { id: 'email', label: 'Email', default: true },
  { id: 'round1', label: 'Round 1', default: true },
  { id: 'round2', label: 'Round 2', default: true },
  { id: 'round3', label: 'Round 3', default: true },
  { id: 'round4', label: 'Round 4', default: true },
  { id: 'extra', label: 'Extra (off grid)', default: true },
  { id: 'meetActiveRounds', label: 'Meet jumps (counted)', default: true },
];

/** Default load names per plane for load labeling (Shark 1, Gypsy 1, etc.). */
const DEFAULT_LOAD_NAMES = { A: 'Shark', B: 'Gypsy' };

/** Stable fallback when manifest has no `planes` (avoids new array each render). */
const DEFAULT_MANIFEST_PLANES = [
  { id: 'A', label: 'Plane A', firstTakeoff: '08:30' },
  { id: 'B', label: 'Plane B', firstTakeoff: '08:35' },
];

const SINGLE_PLANE_LETTER_RE = /^[A-Za-z]$/;

function sortPlaneIds(ids) {
  const arr = [...new Set(ids.map((id) => String(id || '').trim()).filter(Boolean))];
  const letters = arr.filter((id) => SINGLE_PLANE_LETTER_RE.test(id));
  letters.sort((a, b) => a.toUpperCase().localeCompare(b.toUpperCase()));
  const rest = arr.filter((id) => !SINGLE_PLANE_LETTER_RE.test(id));
  rest.sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  return [...letters, ...rest];
}

/**
 * Merge stored `manifest.planes` with any plane ids present on slots so no column is orphaned.
 */
function buildEffectivePlanes(manifest) {
  const stored =
    manifest?.planes && manifest.planes.length > 0 ? manifest.planes : [...DEFAULT_MANIFEST_PLANES];
  const slotPlaneIds = new Set();
  for (const s of manifest?.slots || []) {
    if (s?.planeId != null && String(s.planeId).trim() !== '') {
      slotPlaneIds.add(String(s.planeId));
    }
  }
  const storedById = new Map(stored.map((p) => [String(p.id), { ...p }]));
  const result = [];
  const seen = new Set();
  for (const p of stored) {
    const id = String(p.id);
    result.push(storedById.get(id));
    seen.add(id);
  }
  for (const id of sortPlaneIds([...slotPlaneIds])) {
    if (!seen.has(id)) {
      result.push({ id, label: `Plane ${id}`, firstTakeoff: '' });
      seen.add(id);
    }
  }
  return result;
}

function countActiveParticipantsInSlot(slot) {
  return slot?.participantEmails?.length || 0;
}

/** Only persisted labels (round-specific or legacy all-round), else empty. */
function getStoredLoadLabelForCycle(planeId, loadIndex, cycleIndex, loadMetadata, planes) {
  const meta = loadMetadata[`${planeId}:${loadIndex}`] || {};
  const c = String(cycleIndex);
  const by = meta.labelsByRound;
  if (by && by[c] != null && String(by[c]).trim() !== '') return String(by[c]).trim();
  if (meta.label != null && String(meta.label).trim() !== '') return String(meta.label).trim();
  return '';
}

/**
 * Display label for one plane/load for a given manifest round (cycle).
 * Stored label if any, else global sequence suggest (when manifest given), else plane name + load index.
 */
function getLoadLabelForCycle(planeId, loadIndex, cycleIndex, loadMetadata, planes, manifest) {
  const stored = getStoredLoadLabelForCycle(planeId, loadIndex, cycleIndex, loadMetadata, planes);
  if (stored) return stored;
  if (manifest) {
    return suggestPlaneLoadLabel({
      manifest,
      planeId,
      loadIndex,
      planes,
      cycleIndex,
    });
  }
  const loadName = DEFAULT_LOAD_NAMES[planeId] || planes.find((pl) => pl.id === planeId)?.label || planeId;
  return `${loadName} ${loadIndex}`;
}

function getPlaneNamingState(manifest) {
  return manifest?.planeNamingState || {};
}

function getSwapMappedPlaneName(baseName, loadIndex, swapTimeline) {
  let name = baseName;
  for (const s of swapTimeline || []) {
    const at = Number(s?.effectiveAtLoad || 0);
    if (!at || Number(loadIndex) < at) continue;
    if ((s?.from || '').toLowerCase() === String(name || '').toLowerCase()) {
      name = s?.to || name;
    }
  }
  return name;
}

function suggestPlaneLoadLabel({ manifest, planeId, loadIndex, planes, cycleIndex }) {
  const naming = getPlaneNamingState(manifest);
  const overrides = naming.labelOverrides || {};
  const overrideKey = `${planeId}:${loadIndex}`;
  if (String(overrides[overrideKey] || '').trim()) return String(overrides[overrideKey]).trim();
  const planeNameById = naming.planeNamesById || {};
  const defaultPlaneName =
    planeNameById[planeId] ||
    DEFAULT_LOAD_NAMES[planeId] ||
    planes.find((p) => String(p.id) === String(planeId))?.label ||
    String(planeId);
  const finalPlaneName = getSwapMappedPlaneName(defaultPlaneName, loadIndex, naming.swapTimeline || []);
  const numByLoad = naming.numberByLoadKey || {};
  const slots = manifest?.slots || [];
  const cycles = manifest?.cycles;
  let n;
  if (numByLoad[overrideKey] != null && String(numByLoad[overrideKey]).trim() !== '') {
    n = Number(numByLoad[overrideKey]);
  } else if (cycleIndex != null && Number.isFinite(Number(cycleIndex))) {
    n = globalSequenceNumber(planeId, loadIndex, Number(cycleIndex), slots, cycles);
  } else {
    n = Number(loadIndex);
  }
  return `${finalPlaneName} ${n}`;
}

function toDomSafeIdPart(v) {
  return String(v ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** Parse load label like "Shark 1" or "Super 5" into { name, number }. */
function parseLoadLabel(label) {
  if (!label || typeof label !== 'string') return null;
  const m = label.trim().match(/^(.+?)\s*(\d+)$/);
  if (!m) return null;
  return { name: m[1].trim(), number: parseInt(m[2], 10) };
}

/** Max load index for a plane in one round from slots (minimum 1). */
function maxLoadIndexForPlaneRound(slots, planeId, round) {
  let m = 0;
  for (const s of slots || []) {
    if (String(s.planeId ?? '') !== String(planeId ?? '')) continue;
    if (Number(s.cycleIndex) !== Number(round)) continue;
    m = Math.max(m, Number(s.loadIndex) || 0);
  }
  return Math.max(1, m);
}

/** Max load index in a round across all planes (matches planner column count for that round). */
function maxLoadIndexForRoundAllPlanes(slots, round) {
  let m = 0;
  for (const s of slots || []) {
    if (Number(s.cycleIndex) !== Number(round)) continue;
    m = Math.max(m, Number(s.loadIndex) || 0);
  }
  return Math.max(1, m);
}

/**
 * Loads per round for naming / populate order: at least this plane’s slot usage, and at least the
 * round’s global max (so a plane with no slots yet still gets Shark 1–3 when other planes use 3 loads).
 */
function loadsPerRoundForPlaneSequence(slots, planeId, round) {
  return Math.max(
    maxLoadIndexForPlaneRound(slots, planeId, round),
    maxLoadIndexForRoundAllPlanes(slots, round)
  );
}

function edmMaxCycles(cycles) {
  return Math.min(Math.max(Number(cycles) || 4, 1), 12);
}

/** Every (round, loadIndex) cell for one plane in day order. */
function orderedPlaneLoadCells(planeId, slots, cycles) {
  const cMax = edmMaxCycles(cycles);
  const cells = [];
  for (let r = 1; r <= cMax; r++) {
    const nLoads = loadsPerRoundForPlaneSequence(slots, planeId, r);
    for (let li = 1; li <= nLoads; li++) {
      cells.push({ round: r, loadIndex: li });
    }
  }
  return cells;
}

function globalSequenceNumber(planeId, loadIndex, cycleIndex, slots, cycles) {
  const R = Number(cycleIndex);
  const L = Number(loadIndex);
  let seq = 0;
  for (let r = 1; r < R; r++) {
    seq += loadsPerRoundForPlaneSequence(slots, planeId, r);
  }
  return seq + L;
}

function cellsAfterAnchor(planeId, anchorRound, anchorLoadIndex, slots, cycles) {
  const ordered = orderedPlaneLoadCells(planeId, slots, cycles);
  const ar = Number(anchorRound);
  const al = Number(anchorLoadIndex);
  const idx = ordered.findIndex((c) => c.round === ar && c.loadIndex === al);
  if (idx < 0) return [];
  return ordered.slice(idx + 1);
}

/** Remove undefined values from an object (and nested objects/arrays) so Firestore accepts it. */
function stripUndefined(obj) {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, stripUndefined(v)])
  );
}

/** Mentor + freshlings, same shape as populateEventManifest. */
function participantEmailsFromTeam(team) {
  const mentor = (team?.mentor || '').toString().trim();
  const freshlings = Array.isArray(team?.freshlings)
    ? team.freshlings.map((e) => (e || '').toString().trim()).filter(Boolean)
    : [];
  return [mentor].filter(Boolean).concat(freshlings);
}

function participantEmailKey(email) {
  return (email || '').toLowerCase().trim();
}

/**
 * Mentor / freshling for manifest UI, print, and exports.
 * Uses `flyers[].type` when set; otherwise falls back to the slot's team roster
 * (`mentor` / `freshlings`) so people still classify correctly without a flyer doc.
 */
function participantRoleForSlot(slot, email, team, flyersByEmail) {
  const key = participantEmailKey(email);
  if (!key) return '';
  const flyer = flyersByEmail?.get(key);
  const fromFlyer = String(flyer?.type || '').toLowerCase().trim();
  if (fromFlyer === 'mentor' || fromFlyer === 'freshling') return fromFlyer;
  if (team) {
    const mentorKey = participantEmailKey(team.mentor);
    if (mentorKey && mentorKey === key) return 'mentor';
    const fresh = Array.isArray(team.freshlings) ? team.freshlings : [];
    for (const raw of fresh) {
      if (participantEmailKey(raw) === key) return 'freshling';
    }
  }
  return '';
}

function numEq(a, b) {
  return Number(a) === Number(b);
}

/** Same plane, load, slot position, and round (Firestore may store numbers as strings). */
function slotsMatchSameCell(s, slot) {
  if (!s || !slot) return false;
  return (
    String(s.planeId ?? '') === String(slot.planeId ?? '') &&
    numEq(s.loadIndex, slot.loadIndex) &&
    numEq(s.slotIndexInLoad, slot.slotIndexInLoad) &&
    numEq(s.cycleIndex, slot.cycleIndex)
  );
}

function slotMatchesCycle(s, cycleIndex) {
  return numEq(s.cycleIndex, cycleIndex);
}

/** One canonical row per grid cell + round (last write wins) — avoids double counts when Firestore has duplicate slot docs. */
function manifestSlotDedupeKey(s) {
  if (!s) return '';
  return `${String(s.planeId ?? '')}:${Number(s.loadIndex)}:${Number(s.slotIndexInLoad)}:${Number(s.cycleIndex)}`;
}

/** Same physical slot on the grid (any round). */
function sameManifestSlotPosition(s, planeId, loadIndex, slotIndexInLoad) {
  return (
    String(s.planeId ?? '') === String(planeId ?? '') &&
    numEq(s.loadIndex, loadIndex) &&
    numEq(s.slotIndexInLoad, slotIndexInLoad)
  );
}

/**
 * Canonical row for a grid cell + round: last matching entry in `slots` array order.
 * Matches `slotsByKey` (later docs overwrite earlier). Needed when Firestore has duplicate
 * slot rows — `.some(s => s.teamId)` would block + Team while the UI shows the last (empty) row.
 */
function lastSlotForCellAndCycle(slots, planeId, loadIndex, slotIndexInLoad, cycleIndex) {
  let last = null;
  for (const s of slots || []) {
    if (
      sameManifestSlotPosition(s, planeId, loadIndex, slotIndexInLoad) &&
      numEq(s.cycleIndex, cycleIndex)
    ) {
      last = s;
    }
  }
  return last;
}

/** Each slot listing counts as one jump; extra (off-grid) jumps always count. */
function buildManifestJumpCountByEmail(manifest) {
  const countByEmail = new Map();
  const byCell = new Map();
  for (const slot of manifest?.slots || []) {
    byCell.set(manifestSlotDedupeKey(slot), slot);
  }
  for (const slot of byCell.values()) {
    for (const email of slot.participantEmails || []) {
      const key = (email || '').toLowerCase().trim();
      if (key) countByEmail.set(key, (countByEmail.get(key) || 0) + 1);
    }
  }
  for (const jump of manifest?.otherJumps || []) {
    for (const email of jump.participantEmails || []) {
      const key = (email || '').toLowerCase().trim();
      if (key) countByEmail.set(key, (countByEmail.get(key) || 0) + 1);
    }
  }
  return countByEmail;
}

function normalizeAuditPlaneKey(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function derivePlaneNameFromLoadName(loadName, fallback) {
  const text = String(loadName || '').trim();
  if (!text) return String(fallback || '').trim() || 'Plane';
  const m = text.match(/^(.*?)(?:\s+\d+)?$/);
  const base = String(m?.[1] || text).trim();
  return base || String(fallback || '').trim() || text;
}

function getAuditPlaneTheme(planeName, planeId) {
  const key = normalizeAuditPlaneKey(planeName || planeId);
  if (key.includes('shark') || String(planeId || '').toUpperCase() === 'A') {
    return {
      color: 'var(--plane-shark-color)',
      bg: 'var(--plane-shark-bg)',
      border: 'var(--plane-shark-border)',
      text: 'var(--plane-shark-text)',
      chipClass: 'audit-plane-chip--shark',
      pillKey: 'shark',
    };
  }
  if (key.includes('gypsy') || String(planeId || '').toUpperCase() === 'B') {
    return {
      color: 'var(--plane-gypsy-color)',
      bg: 'var(--plane-gypsy-bg)',
      border: 'var(--plane-gypsy-border)',
      text: 'var(--plane-gypsy-text)',
      chipClass: 'audit-plane-chip--gypsy',
      pillKey: 'gypsy',
    };
  }
  return {
    color: 'var(--plane-c-color)',
    bg: 'var(--plane-c-bg)',
    border: 'var(--plane-c-border)',
    text: 'var(--plane-c-text)',
    chipClass: 'audit-plane-chip--plane-c',
    pillKey: 'plane-c',
  };
}

function EventDayManifest() {
  const [searchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [flyers, setFlyers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [populating, setPopulating] = useState(false);
  const [declaringJumpDone, setDeclaringJumpDone] = useState(false);
  const [selectedRound, setSelectedRound] = useState(1);
  const [dragState, setDragState] = useState(null); // participant | team | teamFromOther | offPlane | offPlaneTeam
  const dragStateRef = useRef(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragOverOtherIdx, setDragOverOtherIdx] = useState(null);
  /** `${planeId}:${loadIndex}` while dragging over a Round planner load cell (Shark 1, etc.). */
  const [dragOverPlannerKey, setDragOverPlannerKey] = useState(null);
  const [otherSectionExpanded, setOtherSectionExpanded] = useState(false);
  const [editingOtherJumpLabel, setEditingOtherJumpLabel] = useState({}); // idx -> string
  const [editingLoadLabel, setEditingLoadLabel] = useState({}); // "planeId:loadIndex" -> string
  const [, setSummarySectionExpanded] = useState(true);
  /** Day summary table row order within each round; print plane order uses the same setting. */
  const [summaryLiftOrder] = useState('byLoad'); // 'byLoad' | 'byPlane'
  const [edmExport, setEdmExport] = useState(null); // { kind: 'planeOrder'|'jumpCounts'|'roster', mode } | null
  const [edmExportCols, setEdmExportCols] = useState(() => new Set());
  const [dispatchViewMode, setDispatchViewMode] = useState('dispatch'); // 'dispatch' | 'audit'
  const [dispatchHeaderExpanded, setDispatchHeaderExpanded] = useState(true);
  /** Last-selected print / export format in the manifest output button group. */
  const [manifestPrintOutputView, setManifestPrintOutputView] = useState(null); // 'planeOrder'|'jumpCounts'|'planeTable'|'jumpTable'|'jumpLog'
  /** Dispatch grid per plane+load UI (collapse / done), keyed `${loadIndex}:${planeId}`; persisted per event + round. */
  const [loadGridUI, setLoadGridUI] = useState(() => ({}));
  const [rosterFilter] = useState('all'); // 'all' | 'missingMeet'
  const [rosterIncludeRegistered] = useState(false);
  const [dispatcherSearch, setDispatcherSearch] = useState('');
  const [dispatcherSelectedEmailKey, setDispatcherSelectedEmailKey] = useState('');
  const [dispatcherCardEmailKey, setDispatcherCardEmailKey] = useState('');
  const [dispatcherDropdownOpen, setDispatcherDropdownOpen] = useState(false);
  const [dispatcherHighlightIndex, setDispatcherHighlightIndex] = useState(0);
  const dispatchSearchWrapRef = useRef(null);
  const [destructiveUndo, setDestructiveUndo] = useState(null);
  const destructiveUndoRef = useRef(null);
  const [highlightedSlotKey, setHighlightedSlotKey] = useState('');
  const [highlightedEmailKey, setHighlightedEmailKey] = useState('');
  const [pendingJumpTarget, setPendingJumpTarget] = useState(null);
  const [auditPlaneCapacities, setAuditPlaneCapacities] = useState({});

  useEffect(() => {
    dragStateRef.current = dragState;
    if (!dragState) setDragOverPlannerKey(null);
  }, [dragState]);

  const manifestRef = useRef(manifest);
  useEffect(() => {
    manifestRef.current = manifest;
  }, [manifest]);

  useEffect(() => {
    if (!selectedEventId) return;
    const storageKey = `edm.dispatchSetupExpanded.${selectedEventId}`;
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw === '0') setDispatchHeaderExpanded(false);
      else if (raw === '1') setDispatchHeaderExpanded(true);
    } catch {
      // ignore storage failures
    }
  }, [selectedEventId]);

  useEffect(() => {
    if (!selectedEventId) return;
    const storageKey = `edm.dispatchSetupExpanded.${selectedEventId}`;
    try {
      localStorage.setItem(storageKey, dispatchHeaderExpanded ? '1' : '0');
    } catch {
      // ignore storage failures
    }
  }, [selectedEventId, dispatchHeaderExpanded]);

  useEffect(() => {
    if (dispatchViewMode === 'audit') setSummarySectionExpanded(true);
  }, [dispatchViewMode]);

  useEffect(() => {
    destructiveUndoRef.current = destructiveUndo;
  }, [destructiveUndo]);

  const flushPendingDestructive = useCallback(() => {
    const u = destructiveUndoRef.current;
    if (!u) return;
    clearTimeout(u.timerId);
    u.commit();
    setDestructiveUndo(null);
  }, []);

  useEffect(() => {
    flushPendingDestructive();
  }, [selectedRound, dispatchViewMode, flushPendingDestructive]);

  useEffect(() => {
    return () => {
      const u = destructiveUndoRef.current;
      if (u) {
        clearTimeout(u.timerId);
        u.commit();
      }
    };
  }, []);

  useEffect(() => {
    function onDocMouseDown(e) {
      if (dispatcherDropdownOpen) {
        const el = dispatchSearchWrapRef.current;
        if (el && !el.contains(e.target)) setDispatcherDropdownOpen(false);
      }
    }
    function onDocKeyDown(e) {
      if (e.key === 'Escape') {
        setDispatcherDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [dispatcherDropdownOpen]);

  useEffect(() => {
    async function fetchEvents() {
      try {
        const snap = await getDocs(collection(firestore, 'events'));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
      } catch (e) {
        setError(e.message || 'Failed to load events');
      }
    }
    fetchEvents();
  }, []);

  useEffect(() => {
    if (!events.length) return;
    const fromUrl = (searchParams.get('event') || searchParams.get('eventId') || '').trim();
    if (fromUrl && events.some((e) => e.id === fromUrl)) {
      setSelectedEventId(fromUrl);
      return;
    }
    let cancelled = false;
    (async () => {
      const active = await getActiveEventId();
      if (cancelled) return;
      setSelectedEventId((prev) => {
        if (prev && events.some((e) => e.id === prev)) return prev;
        return resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: active, events });
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [events, searchParams]);

  useEffect(() => {
    if (!selectedEventId) return;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const [flyerSnap, manifestSnap, teamsSnap] = await Promise.all([
          getDocs(
            query(
              collection(firestore, 'flyers'),
              where('eventId', '==', selectedEventId)
            )
          ),
          getDoc(doc(firestore, 'eventManifest', selectedEventId)),
          getDocs(
            query(collection(firestore, 'teams'), where('eventId', '==', selectedEventId))
          ),
        ]);
        setFlyers(flyerSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setManifest(manifestSnap.exists ? { id: manifestSnap.id, ...manifestSnap.data() } : null);
        setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        setError(e.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedEventId]);

  const flyersByEmail = useMemo(() => {
    const map = new Map();
    flyers.forEach((f) => {
      const email = (f.email || '').toLowerCase().trim();
      if (email) map.set(email, f);
    });
    return map;
  }, [flyers]);

  const teamsSorted = useMemo(() => {
    return [...teams].sort((a, b) =>
      String(a.teamNumber ?? '')
        .localeCompare(String(b.teamNumber ?? ''), undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [teams]);

  const teamNumberByEmail = useMemo(() => {
    const out = new Map();
    for (const t of teams) {
      const teamNum = String(t.teamNumber || '?');
      for (const e of participantEmailsFromTeam(t)) {
        const k = (e || '').toLowerCase().trim();
        if (k) out.set(k, teamNum);
      }
    }
    return out;
  }, [teams]);

  const planes = useMemo(() => buildEffectivePlanes(manifest), [manifest]);
  const planeNamingState = useMemo(() => getPlaneNamingState(manifest), [manifest]);

  const { slotsByKey, loadIndices, teamsPerLoad, peopleCountByCell, teamsCountByCell } = useMemo(() => {
    const slots = manifest?.slots || [];
    const cycleSlots = slots.filter((s) => slotMatchesCycle(s, selectedRound));
    const byKey = {};
    let maxLoad = 0;
    let maxSlot = 0;
    cycleSlots.forEach((s) => {
      const key = `${s.planeId}:${s.loadIndex}:${s.slotIndexInLoad}`;
      byKey[key] = s;
      if (s.loadIndex > maxLoad) maxLoad = s.loadIndex;
      if (s.slotIndexInLoad > maxSlot) maxSlot = s.slotIndexInLoad;
    });
    const peopleCountByCell = {};
    const teamsCountByCell = {};
    Object.values(byKey).forEach((s) => {
      const cellKey = `${s.planeId}:${s.loadIndex}`;
      peopleCountByCell[cellKey] =
        (peopleCountByCell[cellKey] || 0) + countActiveParticipantsInSlot(s);
      if (s.teamId) {
        teamsCountByCell[cellKey] = (teamsCountByCell[cellKey] || 0) + 1;
      }
    });
    const loadIndices = [];
    for (let i = 1; i <= maxLoad; i++) loadIndices.push(i);
    return {
      slotsByKey: byKey,
      loadIndices,
      teamsPerLoad: maxSlot || 7,
      peopleCountByCell,
      teamsCountByCell,
    };
  }, [manifest?.slots, selectedRound]);

  const loadMetadata = useMemo(() => manifest?.loadMetadata || {}, [manifest?.loadMetadata]);

  const ensureRoundPlaneLoadSlots = async (roundNum, planeId, loadIndex) => {
    if (!manifest || !selectedEventId) return;
    const existing = (manifest.slots || []).some(
      (s) =>
        String(s.planeId) === String(planeId) &&
        numEq(s.loadIndex, loadIndex) &&
        numEq(s.cycleIndex, roundNum)
    );
    if (existing) return;
    const count = Math.max(1, teamsPerLoad || 7);
    const additions = [];
    for (let slotIndexInLoad = 1; slotIndexInLoad <= count; slotIndexInLoad++) {
      additions.push({
        planeId,
        loadIndex,
        slotIndexInLoad,
        cycleIndex: roundNum,
        participantEmails: [],
      });
    }
    const nextSlots = [...(manifest.slots || []), ...additions];
    await setDoc(
      doc(firestore, 'eventManifest', selectedEventId),
      stripUndefined({ ...manifest, slots: nextSlots }),
      { merge: true }
    );
    setManifest((prev) => (prev ? { ...prev, slots: nextSlots } : null));
  };

  const handleAddLoadToPlaneRow = async (roundNum, planeId) => {
    const inRound = (manifest?.slots || []).filter(
      (s) => String(s.planeId) === String(planeId) && numEq(s.cycleIndex, roundNum)
    );
    const maxLoad = inRound.reduce((m, s) => Math.max(m, Number(s.loadIndex) || 0), 0);
    const nextLoad = Math.max(1, maxLoad + 1);
    try {
      await ensureRoundPlaneLoadSlots(roundNum, planeId, nextLoad);
      setSelectedRound(roundNum);
      setDispatchHeaderExpanded(true);
    } catch (e) {
      setError(e.message || 'Failed to add load');
    }
  };

  const handleAddPlaneRow = async () => {
    if (!manifest || !selectedEventId) return;
    const used = new Set((planes || []).map((p) => String(p.id)));
    let nextId = null;
    for (const c of 'ABCDEFGHIJKLMNOPQRSTUVWXYZ') {
      if (!used.has(c)) {
        nextId = c;
        break;
      }
    }
    if (!nextId) return;
    const nextPlanes = [...(manifest.planes || planes), { id: nextId, label: `Plane ${nextId}`, firstTakeoff: '' }];
    try {
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, planes: nextPlanes }),
        { merge: true }
      );
      setManifest((prev) => (prev ? { ...prev, planes: nextPlanes } : null));
      await ensureRoundPlaneLoadSlots(selectedRound, nextId, 1);
      setDispatchHeaderExpanded(true);
    } catch (e) {
      setError(e.message || 'Failed to add plane');
    }
  };

  const handleDeletePlaneRow = async (planeId) => {
    if (!manifest || !selectedEventId) return;
    const allPlaneSlots = (manifest.slots || []).filter((s) => String(s.planeId) === String(planeId));
    const roundPlaneSlots = allPlaneSlots.filter((s) => numEq(s.cycleIndex, selectedRound));
    const hasAssignedPeople = allPlaneSlots.some((s) => (s.participantEmails || []).length > 0 || s.teamId);
    const hasRoundContent = roundPlaneSlots.some((s) => (s.participantEmails || []).length > 0 || s.teamId);
    const msg = hasAssignedPeople
      ? `Delete plane row ${planeId}? It has assigned teams/people (including future rounds).`
      : hasRoundContent
        ? `Delete plane row ${planeId} for this meet setup?`
        : `Delete plane row ${planeId}?`;
    if (!window.confirm(msg)) return;

    const nextSlots = (manifest.slots || []).filter((s) => String(s.planeId) !== String(planeId));
    const nextPlanes = (manifest.planes || planes).filter((p) => String(p.id) !== String(planeId));

    const namesById = { ...(planeNamingState.planeNamesById || {}) };
    delete namesById[planeId];
    const numberByLoadKey = { ...(planeNamingState.numberByLoadKey || {}) };
    Object.keys(numberByLoadKey).forEach((k) => {
      if (k.startsWith(`${planeId}:`)) delete numberByLoadKey[k];
    });
    const labelOverrides = { ...(planeNamingState.labelOverrides || {}) };
    Object.keys(labelOverrides).forEach((k) => {
      if (k.startsWith(`${planeId}:`)) delete labelOverrides[k];
    });
    const swapTimeline = (planeNamingState.swapTimeline || []).filter(
      (s) => String(s.from || '').toLowerCase() !== String(planeId).toLowerCase() &&
             String(s.to || '').toLowerCase() !== String(planeId).toLowerCase()
    );
    const nextNaming = {
      ...planeNamingState,
      planeNamesById: namesById,
      numberByLoadKey,
      labelOverrides,
      swapTimeline,
    };

    try {
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, planes: nextPlanes, slots: nextSlots, planeNamingState: nextNaming }),
        { merge: true }
      );
      setManifest((prev) =>
        prev ? { ...prev, planes: nextPlanes, slots: nextSlots, planeNamingState: nextNaming } : null
      );
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to delete plane row');
    }
  };

  const getLoadLabel = (planeId, loadIndex) =>
    getLoadLabelForCycle(planeId, loadIndex, selectedRound, loadMetadata, planes, manifest);

  const getPeopleCount = (planeId, loadIndex) => {
    return peopleCountByCell[`${planeId}:${loadIndex}`] || 0;
  };

  const getTeamsCount = (planeId, loadIndex) => teamsCountByCell[`${planeId}:${loadIndex}`] ?? 0;

  const getDisplaySlotRows = (planeId, loadIndex) => {
    const contentSlots = Object.values(slotsByKey)
      .filter(
        (s) =>
          String(s.planeId ?? '') === String(planeId ?? '') &&
          numEq(s.loadIndex, loadIndex) &&
          (s.teamId || (s.participantEmails || []).length > 0)
      )
      .sort((a, b) => (Number(a.slotIndexInLoad) || 0) - (Number(b.slotIndexInLoad) || 0));
    const maxUsed = contentSlots.reduce((m, s) => Math.max(m, Number(s.slotIndexInLoad) || 0), 0);
    const nextSlotIdx = Math.max(1, maxUsed + 1);
    return [
      ...contentSlots.map((s) => ({ slotIdx: Number(s.slotIndexInLoad) || 1, slot: s })),
      { slotIdx: nextSlotIdx, slot: null },
    ];
  };

  const getDefaultLoadLabel = (planeId, loadIndex) =>
    suggestPlaneLoadLabel({
      manifest,
      planeId,
      loadIndex,
      planes,
      cycleIndex: selectedRound,
    });

  const loadSummaryRows = useMemo(() => {
    const slots = manifest?.slots;
    if (!slots?.length) return [];
    const cycles = manifest.cycles || 4;
    const byDedupe = new Map();
    for (const s of slots) {
      byDedupe.set(manifestSlotDedupeKey(s), s);
    }
    const uniqueSlots = Array.from(byDedupe.values());
    let maxLoad = 0;
    for (const s of uniqueSlots) {
      if (s.loadIndex > maxLoad) maxLoad = s.loadIndex;
    }
    const rows = [];
    for (const p of planes) {
      for (let loadIndex = 1; loadIndex <= maxLoad; loadIndex++) {
        const hasAny = uniqueSlots.some(
          (s) => String(s.planeId) === String(p.id) && numEq(s.loadIndex, loadIndex)
        );
        if (!hasAny) continue;
        const byRound = Array.from({ length: cycles }, () => 0);
        for (const s of uniqueSlots) {
          if (String(s.planeId) !== String(p.id) || !numEq(s.loadIndex, loadIndex)) continue;
          const r = Number(s.cycleIndex);
          if (r >= 1 && r <= cycles) byRound[r - 1] += countActiveParticipantsInSlot(s);
        }
        const total = byRound.reduce((a, b) => a + b, 0);
        rows.push({ planeId: p.id, planeLabel: p.label, loadIndex, byRound, total });
      }
    }
    return rows;
  }, [manifest, planes]);

  const manifestJumpCountByEmail = useMemo(
    () => buildManifestJumpCountByEmail(manifest),
    [manifest]
  );

  /** Fury-backed events must have `furyJumpCompletionEventOfferingId` on the event doc before Declare can POST. */
  const furyDeclareOfferingMisconfigured = useMemo(() => {
    if (!selectedEventId) return false;
    const ev = events.find((x) => x.id === selectedEventId);
    if (!ev || ev.registrationImportSource !== 'fury_api') return false;
    return !(ev.furyJumpCompletionEventOfferingId || '').toString().trim();
  }, [events, selectedEventId]);

  const edmPlaneOrderExportRows = useMemo(() => {
    const slots = manifest?.slots || [];
    if (!slots.length) return [];
    const planeIndex = new Map((planes || []).map((p, idx) => [p.id, idx]));
    const rows = slots
      .filter((s) => s.teamId)
      .slice()
      .sort((a, b) => {
        if (a.cycleIndex !== b.cycleIndex) return a.cycleIndex - b.cycleIndex;
        if (summaryLiftOrder === 'byPlane') {
          const ai = planeIndex.get(a.planeId) ?? 999;
          const bi = planeIndex.get(b.planeId) ?? 999;
          if (ai !== bi) return ai - bi;
          if (a.loadIndex !== b.loadIndex) return a.loadIndex - b.loadIndex;
          return a.slotIndexInLoad - b.slotIndexInLoad;
        }
        if (a.loadIndex !== b.loadIndex) return a.loadIndex - b.loadIndex;
        const ai = planeIndex.get(a.planeId) ?? 999;
        const bi = planeIndex.get(b.planeId) ?? 999;
        if (ai !== bi) return ai - bi;
        return a.slotIndexInLoad - b.slotIndexInLoad;
      })
      .map((slot) => {
        const team = teams.find((t) => String(t.id) === String(slot.teamId));
        const people = (slot.participantEmails || [])
          .map((email) => {
            const k = (email || '').toLowerCase().trim();
            const f = flyersByEmail.get(k);
            const role = participantRoleForSlot(slot, email, team, flyersByEmail);
            const type = role || String(f?.type || '').toLowerCase();
            return {
              type,
              name: f ? getDisplayName(f) || f.email || email : email,
            };
          })
          .filter((p) => p.name);
        const mentors = people.filter((p) => p.type === 'mentor').map((p) => p.name);
        const freshlings = people.filter((p) => p.type === 'freshling').map((p) => p.name);
        const others = people
          .filter((p) => p.type !== 'mentor' && p.type !== 'freshling')
          .map((p) => p.name);
        return {
          round: String(slot.cycleIndex ?? ''),
          plane: planes.find((p) => p.id === slot.planeId)?.label || String(slot.planeId ?? ''),
          load: getLoadLabelForCycle(
            slot.planeId,
            slot.loadIndex,
            slot.cycleIndex,
            loadMetadata,
            planes,
            manifest
          ),
          teamNumber: String(slot.teamNumber || '?'),
          mentor: mentors.length ? mentors.join(', ') : '—',
          freshlings: [...freshlings, ...others].length ? [...freshlings, ...others].join(', ') : '—',
        };
      });
    return rows;
  }, [manifest, summaryLiftOrder, planes, flyersByEmail, loadMetadata, teams]);

  const edmJumpCountExportRows = useMemo(() => {
    const countByEmail = buildManifestJumpCountByEmail(manifest);
    if (countByEmail.size === 0) return [];
    const displayEmailByLower = new Map();
    for (const slot of manifest?.slots || []) {
      for (const email of slot.participantEmails || []) {
        const key = (email || '').toLowerCase().trim();
        if (key && !displayEmailByLower.has(key)) displayEmailByLower.set(key, String(email).trim());
      }
    }
    for (const jump of manifest?.otherJumps || []) {
      for (const email of jump.participantEmails || []) {
        const key = (email || '').toLowerCase().trim();
        if (key && !displayEmailByLower.has(key)) displayEmailByLower.set(key, String(email).trim());
      }
    }
    return Array.from(countByEmail.entries())
      .map(([key, jumps]) => {
        const f = flyersByEmail.get(key);
        const email = displayEmailByLower.get(key) || key;
        const name = f ? getDisplayName(f) || email : email;
        return { name, email, jumps: String(jumps) };
      })
      .sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
  }, [manifest, flyersByEmail]);

  const rosterJumpLogRowsRaw = useMemo(() => {
    const cycles = Math.min(Math.max(manifest?.cycles || 4, 1), 12);
    const md = manifest?.loadMetadata || {};
    const byDedupe = new Map();
    for (const s of manifest?.slots || []) {
      byDedupe.set(manifestSlotDedupeKey(s), s);
    }
    const uniqueSlots = Array.from(byDedupe.values());
    const emails = new Set();
    for (const slot of uniqueSlots) {
      for (const e of slot.participantEmails || []) {
        const k = (e || '').toLowerCase().trim();
        if (k) emails.add(k);
      }
    }
    for (const jump of manifest?.otherJumps || []) {
      for (const e of jump.participantEmails || []) {
        const k = (e || '').toLowerCase().trim();
        if (k) emails.add(k);
      }
    }
    if (rosterIncludeRegistered) {
      for (const f of flyers) {
        const k = (f.email || '').toLowerCase().trim();
        if (k) emails.add(k);
      }
    }
    const displayEmailByLower = new Map();
    for (const slot of uniqueSlots) {
      for (const email of slot.participantEmails || []) {
        const key = (email || '').toLowerCase().trim();
        if (key && !displayEmailByLower.has(key)) displayEmailByLower.set(key, String(email).trim());
      }
    }
    for (const jump of manifest?.otherJumps || []) {
      for (const email of jump.participantEmails || []) {
        const key = (email || '').toLowerCase().trim();
        if (key && !displayEmailByLower.has(key)) displayEmailByLower.set(key, String(email).trim());
      }
    }
    if (rosterIncludeRegistered) {
      for (const f of flyers) {
        const key = (f.email || '').toLowerCase().trim();
        if (key && !displayEmailByLower.has(key)) displayEmailByLower.set(key, String(f.email || '').trim());
      }
    }

    const rows = [...emails].map((emailKey) => {
      const roundCells = [];
      for (let r = 1; r <= cycles; r++) {
        const parts = [];
        for (const slot of uniqueSlots) {
          if (!numEq(slot.cycleIndex, r)) continue;
          for (const em of slot.participantEmails || []) {
            if ((em || '').toLowerCase().trim() !== emailKey) continue;
            const planeLbl =
              (slot.planeLabel || '').trim() ||
              planes.find((pl) => String(pl.id) === String(slot.planeId))?.label ||
              slot.planeId;
            const loadName = getLoadLabelForCycle(slot.planeId, slot.loadIndex, r, md, planes, manifest);
            parts.push(`T#${slot.teamNumber ?? '?'} · ${planeLbl} · ${loadName}`);
          }
        }
        roundCells.push(parts.length ? parts.join('; ') : '—');
      }
      const extraParts = [];
      (manifest?.otherJumps || []).forEach((jump, jidx) => {
        const has = (jump.participantEmails || []).some(
          (e) => (e || '').toLowerCase().trim() === emailKey
        );
        if (has) extraParts.push((jump.label || '').trim() || `Jump ${jidx + 1}`);
      });
      const extra = extraParts.length ? extraParts.join(', ') : '—';
      let meetActive = 0;
      for (let r = 1; r <= cycles; r++) {
        let hasActive = false;
        for (const slot of uniqueSlots) {
          if (!numEq(slot.cycleIndex, r)) continue;
          for (const em of slot.participantEmails || []) {
            if ((em || '').toLowerCase().trim() !== emailKey) continue;
            hasActive = true;
          }
        }
        if (hasActive) meetActive += 1;
      }
      const f = flyersByEmail.get(emailKey);
      const displayEmail = displayEmailByLower.get(emailKey) || emailKey;
      const name = f ? getDisplayName(f) || displayEmail : displayEmail;
      const row = {
        emailKey,
        name,
        email: displayEmail,
        extra,
        meetActiveRounds: String(meetActive),
      };
      for (let i = 0; i < cycles; i++) {
        row[`round${i + 1}`] = roundCells[i] ?? '—';
      }
      return row;
    });
    rows.sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
    return rows;
  }, [manifest, planes, flyersByEmail, flyers, rosterIncludeRegistered]);

  const rosterJumpLogRows = useMemo(() => {
    const cycles = Math.min(Math.max(manifest?.cycles || 4, 1), 12);
    return rosterJumpLogRowsRaw.filter((row) => {
      if (rosterFilter === 'all') return true;
      if (rosterFilter === 'missingMeet') {
        const n = parseInt(row.meetActiveRounds, 10) || 0;
        return n < cycles;
      }
      return true;
    });
  }, [rosterJumpLogRowsRaw, rosterFilter, manifest?.cycles]);

  const auditRoundCount = Math.min(Math.max(manifest?.cycles || 4, 1), 12);

  const auditLoads = useMemo(() => {
    const md = manifest?.loadMetadata || {};
    const out = [];
    for (let r = 1; r <= auditRoundCount; r++) {
      for (const row of loadSummaryRows) {
        const loadName = getLoadLabelForCycle(row.planeId, row.loadIndex, r, md, planes, manifest);
        const planeName = derivePlaneNameFromLoadName(loadName, row.planeLabel || row.planeId);
        out.push({
          id: `${r}:${row.planeId}:${row.loadIndex}`,
          round: r,
          loadOrder: Number(row.loadIndex) || 0,
          loadName,
          planeId: row.planeId,
          planeName,
          people: Number(row.byRound?.[r - 1] || 0),
        });
      }
    }
    return out;
  }, [auditRoundCount, loadSummaryRows, planes, manifest]);

  const auditRounds = useMemo(() => {
    const rounds = [];
    for (let r = 1; r <= auditRoundCount; r++) {
      const subtotal = auditLoads
        .filter((l) => l.round === r)
        .reduce((sum, l) => sum + (Number(l.people) || 0), 0);
      rounds.push({ number: r, subtotal });
    }
    return rounds;
  }, [auditRoundCount, auditLoads]);

  const auditPlanes = useMemo(() => {
    const byId = new Map();
    for (const load of auditLoads) {
      const id = String(load.planeId || 'plane');
      if (!byId.has(id)) {
        byId.set(id, {
          planeId: id,
          name: load.planeName,
          roundTotals: Array.from({ length: auditRoundCount }, () => 0),
          meetTotal: 0,
        });
      }
      const p = byId.get(id);
      p.name = p.name || load.planeName;
      p.roundTotals[load.round - 1] += Number(load.people) || 0;
      p.meetTotal += Number(load.people) || 0;
    }
    const out = Array.from(byId.values()).map((p) => ({ ...p, ...getAuditPlaneTheme(p.name, p.planeId) }));
    out.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
    return out;
  }, [auditLoads, auditRoundCount]);

  const auditJumperRows = useMemo(() => {
    const md = manifest?.loadMetadata || {};
    const byDedupe = new Map();
    for (const s of manifest?.slots || []) {
      byDedupe.set(manifestSlotDedupeKey(s), s);
    }
    const uniqueSlots = Array.from(byDedupe.values());
    const byEmail = new Map();
    const ensure = (emailKey, displayEmail) => {
      const key = (emailKey || '').toLowerCase().trim();
      if (!key) return null;
      if (!byEmail.has(key)) {
        const flyer = flyersByEmail.get(key);
        const email = displayEmail || flyer?.email || key;
        const name = flyer ? getDisplayName(flyer) || email : email;
        byEmail.set(key, {
          id: key,
          emailKey: key,
          name,
          email,
          roundAssignmentsRaw: Array.from({ length: auditRoundCount }, () => []),
          extraJumps: 0,
        });
      }
      return byEmail.get(key);
    };

    for (const slot of uniqueSlots) {
      const r = Number(slot.cycleIndex) || 0;
      if (r < 1 || r > auditRoundCount) continue;
      const loadName = getLoadLabelForCycle(slot.planeId, slot.loadIndex, r, md, planes, manifest);
      const planeName = derivePlaneNameFromLoadName(
        loadName,
        planes.find((pl) => String(pl.id) === String(slot.planeId))?.label || slot.planeId
      );
      for (const em of slot.participantEmails || []) {
        const row = ensure(em, em);
        if (!row) continue;
        row.roundAssignmentsRaw[r - 1].push({
          planeId: slot.planeId,
          planeName,
          loadName,
        });
      }
    }

    for (const jump of manifest?.otherJumps || []) {
      for (const em of jump.participantEmails || []) {
        const row = ensure(em, em);
        if (!row) continue;
        row.extraJumps += 1;
      }
    }

    const rows = Array.from(byEmail.values()).map((row) => {
      const meetJumpTotal = row.roundAssignmentsRaw.reduce(
        (sum, entries) => sum + (entries?.length || 0),
        0
      );
      const roundAssignments = row.roundAssignmentsRaw.map((entries) => {
        if (!entries || entries.length === 0) return null;
        if (entries.length > 1) {
          return {
            isDouble: true,
            loads: entries.map((x) => x.loadName),
            planeName: entries[0].planeName,
          };
        }
        return {
          isDouble: false,
          loadName: entries[0].loadName,
          planeName: entries[0].planeName,
          planeId: entries[0].planeId,
        };
      });
      const meetJumps = meetJumpTotal;
      return {
        id: row.id,
        name: row.name,
        email: row.email,
        roundAssignments,
        extraJumps: row.extraJumps,
        meetJumps,
      };
    });
    rows.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    return rows;
  }, [manifest, flyersByEmail, auditRoundCount, planes]);

  const dispatcherNameCounts = useMemo(() => {
    const m = new Map();
    for (const row of rosterJumpLogRowsRaw) {
      const k = (row.name || '').trim().toLowerCase();
      if (!k) continue;
      m.set(k, (m.get(k) || 0) + 1);
    }
    return m;
  }, [rosterJumpLogRowsRaw]);

  const dispatcherSearchMatches = useMemo(() => {
    const q = (dispatcherSearch || '').toLowerCase().trim();
    if (!q) return [];
    const md = manifest?.loadMetadata || {};
    const slots = manifest?.slots || [];
    const buildStatus = (emailKey) => {
      const onThisRound = [];
      for (const s of slots) {
        if (!numEq(s.cycleIndex, selectedRound)) continue;
        if ((s.participantEmails || []).some((e) => (e || '').toLowerCase().trim() === emailKey)) {
          onThisRound.push(s);
        }
      }
      if (onThisRound.length) {
        const s = onThisRound[0];
        const loadName = getLoadLabelForCycle(s.planeId, s.loadIndex, selectedRound, md, planes, manifest);
        return { kind: 'on', label: `On load: ${loadName}` };
      }
      const anySlot = slots.some((s) =>
        (s.participantEmails || []).some((e) => (e || '').toLowerCase().trim() === emailKey)
      );
      if (anySlot) {
        return { kind: 'off', label: `Off load — Round ${selectedRound}` };
      }
      return { kind: 'unassigned', label: 'Not yet assigned' };
    };
    const rows = [];
    for (const row of rosterJumpLogRowsRaw) {
      const teamNum = teamNumberByEmail.get(row.emailKey) || '?';
      const hay = `${row.name || ''} ${row.email || ''} ${teamNum} #${teamNum}`.toLowerCase();
      if (!hay.includes(q)) continue;
      const nameKey = (row.name || '').trim().toLowerCase();
      const ambiguous = Boolean(nameKey && (dispatcherNameCounts.get(nameKey) || 0) > 1);
      rows.push({
        ...row,
        teamNumber: teamNum,
        searchStatus: buildStatus(row.emailKey),
        nameAmbiguous: ambiguous,
      });
      if (rows.length >= 8) break;
    }
    return rows;
  }, [dispatcherSearch, rosterJumpLogRowsRaw, teamNumberByEmail, selectedRound, planes, manifest, dispatcherNameCounts]);

  const dispatcherCardData = useMemo(() => {
    const key = (dispatcherCardEmailKey || '').toLowerCase().trim();
    if (!key) return null;
    const row = rosterJumpLogRowsRaw.find((r) => r.emailKey === key);
    if (!row) return null;
    const rounds = [];
    const totalRounds = Math.min(Math.max(manifest?.cycles || 4, 1), 12);
    const md = manifest?.loadMetadata || {};
    const slots = manifest?.slots || [];
    for (let r = 1; r <= totalRounds; r++) {
      const assignments = slots.filter((s) => {
        if (!numEq(s.cycleIndex, r)) return false;
        return (s.participantEmails || []).some((e) => (e || '').toLowerCase().trim() === key);
      });
      rounds.push({ round: r, assignments });
    }
    const onThisRound = [];
    for (const s of slots) {
      if (!numEq(s.cycleIndex, selectedRound)) continue;
      if ((s.participantEmails || []).some((e) => (e || '').toLowerCase().trim() === key)) {
        onThisRound.push(s);
      }
    }
    let cardStatus;
    if (onThisRound.length) {
      const s = onThisRound[0];
      const loadName = getLoadLabelForCycle(s.planeId, s.loadIndex, selectedRound, md, planes, manifest);
      cardStatus = { kind: 'on', label: `On load: ${loadName}` };
    } else if (
      slots.some((s) => (s.participantEmails || []).some((e) => (e || '').toLowerCase().trim() === key))
    ) {
      cardStatus = { kind: 'off', label: `Off load — Round ${selectedRound}` };
    } else {
      cardStatus = { kind: 'unassigned', label: 'Not yet assigned' };
    }
    return {
      ...row,
      teamNumber: teamNumberByEmail.get(key) || '?',
      rounds,
      totalRounds,
      cardStatus,
    };
  }, [dispatcherCardEmailKey, rosterJumpLogRowsRaw, teamNumberByEmail, selectedRound, planes, manifest]);

  useEffect(() => {
    setDispatcherHighlightIndex(0);
  }, [dispatcherSearch]);

  useEffect(() => {
    if (!dispatcherSearchMatches.length) {
      setDispatcherSelectedEmailKey('');
      return;
    }
    const hasSelected = dispatcherSearchMatches.some((m) => m.emailKey === dispatcherSelectedEmailKey);
    if (!hasSelected) setDispatcherSelectedEmailKey(dispatcherSearchMatches[0].emailKey);
  }, [dispatcherSearchMatches, dispatcherSelectedEmailKey]);

  const offPlanePeopleCurrentRound = useMemo(() => {
    const totalRounds = Math.min(Math.max(manifest?.cycles || 4, 1), 12);
    const roundSlots = (manifest?.slots || []).filter((s) => numEq(s.cycleIndex, selectedRound));
    const onPlane = new Set();
    roundSlots.forEach((s) =>
      (s.participantEmails || []).forEach((e) => {
        const k = (e || '').toLowerCase().trim();
        if (k) onPlane.add(k);
      })
    );
    const roundSetByEmail = new Map();
    const roundCountByEmail = new Map();
    for (const s of manifest?.slots || []) {
      const roundNum = Number(s.cycleIndex);
      if (roundNum < 1 || roundNum > totalRounds) continue;
      for (const raw of s.participantEmails || []) {
        const key = (raw || '').toLowerCase().trim();
        if (!key) continue;
        if (!roundSetByEmail.has(key)) roundSetByEmail.set(key, new Set());
        roundSetByEmail.get(key).add(roundNum);
        if (!roundCountByEmail.has(key)) roundCountByEmail.set(key, new Map());
        const counts = roundCountByEmail.get(key);
        counts.set(roundNum, (counts.get(roundNum) || 0) + 1);
      }
    }
    const teamByEmail = new Map();
    for (const t of teams) {
      const group = participantEmailsFromTeam(t);
      for (const e of group) {
        const k = (e || '').toLowerCase().trim();
        if (k) teamByEmail.set(k, t);
      }
    }
    const pool = [];
    for (const [key, t] of teamByEmail.entries()) {
      const f = flyersByEmail.get(key);
      const name = f ? getDisplayName(f) || f.email || key : key;
      const assignedRounds = roundSetByEmail.get(key)?.size || 0;
      const missingRounds = Math.max(0, totalRounds - assignedRounds);
      if (missingRounds <= 0) continue;
      const onSelectedRound = onPlane.has(key);
      const roundCoverage = Array.from({ length: totalRounds }, (_, idx) =>
        roundCountByEmail.get(key)?.get(idx + 1) || 0
      );
      const heavilyMissing =
        onSelectedRound && missingRounds >= Math.ceil(totalRounds / 2);
      pool.push({
        emailKey: key,
        email: f?.email || key,
        name,
        teamId: t.id,
        teamNumber: t.teamNumber || '?',
        assignedRounds,
        missingRounds,
        missingCount: missingRounds,
        totalRounds,
        onSelectedRound,
        onRound: onSelectedRound,
        roundCoverage,
        heavilyMissing,
      });
    }
    pool.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
    return pool;
  }, [manifest?.slots, manifest?.cycles, selectedRound, teams, flyersByEmail]);

  const offPlaneGroupedByTeam = useMemo(() => {
    const byTeam = new Map();
    for (const p of offPlanePeopleCurrentRound) {
      const key = String(p.teamId || 'none');
      if (!byTeam.has(key)) {
        byTeam.set(key, { teamId: p.teamId, teamNumber: p.teamNumber, members: [] });
      }
      byTeam.get(key).members.push(p);
    }
    const rows = Array.from(byTeam.values());
    rows.sort((a, b) =>
      String(a.teamNumber || '').localeCompare(String(b.teamNumber || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    );
    rows.forEach((r) =>
      r.members.sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      )
    );
    return rows;
  }, [offPlanePeopleCurrentRound]);

  const roundPlannerRows = useMemo(() => {
    const rows = [];
    const byPlaneLoad = new Map();
    const inRound = (manifest?.slots || []).filter((s) => numEq(s.cycleIndex, selectedRound));
    let maxLoadInRound = 1;
    for (const s of inRound) {
      maxLoadInRound = Math.max(maxLoadInRound, Number(s.loadIndex) || 1);
      const k = `${s.planeId}:${s.loadIndex}`;
      if (!byPlaneLoad.has(k)) byPlaneLoad.set(k, { people: 0, teams: 0 });
      const row = byPlaneLoad.get(k);
      row.people += (s.participantEmails || []).length;
      if (s.teamId) row.teams += 1;
    }
    for (const p of planes) {
      const loads = [];
      for (let li = 1; li <= maxLoadInRound; li++) {
        const k = `${p.id}:${li}`;
        const counts = byPlaneLoad.get(k) || { people: 0, teams: 0 };
        const label = getLoadLabelForCycle(p.id, li, selectedRound, loadMetadata, planes, manifest);
        loads.push({ loadIndex: li, label, ...counts });
      }
      rows.push({ plane: p, loads });
    }
    return rows;
  }, [manifest, selectedRound, planes, loadMetadata]);

  const loadGridUIStorageKey = useMemo(
    () => (selectedEventId ? `edm.manifest.loadPlaneUi.v2.${selectedEventId}.r${selectedRound}` : ''),
    [selectedEventId, selectedRound]
  );

  useEffect(() => {
    if (!loadGridUIStorageKey) {
      setLoadGridUI({});
      return;
    }
    try {
      const raw = localStorage.getItem(loadGridUIStorageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        setLoadGridUI(parsed && typeof parsed === 'object' ? parsed : {});
      } else {
        setLoadGridUI({});
      }
    } catch {
      setLoadGridUI({});
    }
  }, [loadGridUIStorageKey]);

  const patchLoadGridUI = useCallback(
    (loadIndex, planeId, patch) => {
      const k = `${String(loadIndex)}:${String(planeId)}`;
      setLoadGridUI((prev) => {
        const cur = prev[k] ?? { collapsed: false, done: false };
        const nextEntry = { ...cur, ...patch };
        const next = { ...prev, [k]: nextEntry };
        if (loadGridUIStorageKey) {
          try {
            localStorage.setItem(loadGridUIStorageKey, JSON.stringify(next));
          } catch {
            // ignore quota / private mode
          }
        }
        return next;
      });
    },
    [loadGridUIStorageKey]
  );

  const roundOpsSummary = useMemo(() => {
    const roundSlots = (manifest?.slots || []).filter((s) => numEq(s.cycleIndex, selectedRound));
    let onLoadPeople = 0;
    roundSlots.forEach((s) => {
      onLoadPeople += (s.participantEmails || []).length;
    });
    const offLoadPeople = offPlanePeopleCurrentRound.length;
    return {
      onLoadPeople,
      offLoadPeople,
      totalTracked: onLoadPeople + offLoadPeople,
    };
  }, [manifest?.slots, selectedRound, offPlanePeopleCurrentRound]);

  const getEdmExportColumns = (kind) => {
    if (kind === 'planeOrder') return EDM_PLANE_ORDER_EXPORT_COLS;
    if (kind === 'roster') return EDM_ROSTER_EXPORT_COLS;
    return EDM_JUMP_COUNTS_EXPORT_COLS;
  };

  useEffect(() => {
    if (!edmExport) return;
    const cols = getEdmExportColumns(edmExport.kind);
    setEdmExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  }, [edmExport]);

  useEffect(() => {
    setManifestPrintOutputView(null);
  }, [selectedEventId]);

  useEffect(() => {
    if (!highlightedSlotKey && !highlightedEmailKey) return undefined;
    const t = setTimeout(() => {
      setHighlightedSlotKey('');
      setHighlightedEmailKey('');
    }, 3500);
    return () => clearTimeout(t);
  }, [highlightedSlotKey, highlightedEmailKey]);

  useEffect(() => {
    if (!highlightedSlotKey) return;
    const parts = highlightedSlotKey.split(':');
    if (parts.length < 4) return;
    const [planeId, loadIndex, slotIdx, round] = parts;
    const id = `edm-slot-${round}-${toDomSafeIdPart(planeId)}-${loadIndex}-${slotIdx}`;
    const el = document.getElementById(id);
    el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [highlightedSlotKey]);

  useEffect(() => {
    if (!pendingJumpTarget) return;
    if (!dispatchHeaderExpanded) return;
    if (!numEq(selectedRound, pendingJumpTarget.round)) return;
    const id = `edm-slot-${Number(pendingJumpTarget.round) || 0}-${toDomSafeIdPart(pendingJumpTarget.planeId)}-${Number(pendingJumpTarget.loadIndex) || 0}-${Number(pendingJumpTarget.slotIndexInLoad) || 0}`;
    const el = document.getElementById(id);
    if (!el) return;
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    setPendingJumpTarget(null);
  }, [pendingJumpTarget, selectedRound, dispatchHeaderExpanded]);

  const openEdmExport = (kind, mode) => {
    const rows =
      kind === 'planeOrder'
        ? edmPlaneOrderExportRows
        : kind === 'roster'
          ? rosterJumpLogRows
          : edmJumpCountExportRows;
    if (!rows.length) {
      window.alert(
        kind === 'planeOrder'
          ? 'No assigned teams on the manifest to export.'
          : kind === 'roster'
            ? 'No people to export for the jump log (adjust filters or add people to the manifest).'
            : 'No participants on the manifest to export.'
      );
      return;
    }
    if (kind === 'planeOrder') setManifestPrintOutputView('planeTable');
    else if (kind === 'jumpCounts') setManifestPrintOutputView('jumpTable');
    else setManifestPrintOutputView('jumpLog');
    setEdmExport({ kind, mode });
  };

  const resetEdmExportCols = () => {
    if (!edmExport) return;
    const cols = getEdmExportColumns(edmExport.kind);
    setEdmExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  };

  const runEdmExport = (selectedColumnIds) => {
    if (!edmExport) return;
    const { kind, mode } = edmExport;
    const allCols = getEdmExportColumns(kind);
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : edmExportCols;
    const active = allCols.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const baseRows =
      kind === 'planeOrder'
        ? edmPlaneOrderExportRows
        : kind === 'roster'
          ? rosterJumpLogRows
          : edmJumpCountExportRows;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = baseRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const eventTitle = events.find((ev) => ev.id === selectedEventId)?.title || 'Event';
    const sub =
      kind === 'planeOrder'
        ? 'Day manifest — plane order (table)'
        : kind === 'roster'
          ? 'Day manifest — by person (jump log)'
          : 'Day manifest — jump counts';
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(eventTitle)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} row(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${eventTitle} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setEdmExport(null);
  };

  const handlePopulate = async () => {
    if (!selectedEventId) return;
    setPopulating(true);
    setError('');
    try {
      const fn = httpsCallable(functions, 'populateEventManifest');
      await fn({ eventId: selectedEventId });
      const manifestSnap = await getDoc(doc(firestore, 'eventManifest', selectedEventId));
      setManifest(manifestSnap.exists ? { id: manifestSnap.id, ...manifestSnap.data() } : null);
      setError('');
    } catch (e) {
      setError(e.message || 'Failed to populate manifest');
    } finally {
      setPopulating(false);
    }
  };

  const handleDeclareJumpingDone = async () => {
    if (!selectedEventId || !manifest) return;
    const ev = events.find((x) => x.id === selectedEventId);
    const needsFuryToken = ev?.registrationImportSource === 'fury_api';
    if (needsFuryToken && furyDeclareOfferingMisconfigured) {
      setError(
        'Configure the event before declaring: Admin → Event Management → edit this event → Registration import. ' +
          'Set “Jump completion Fury offering ID” to the Fury event offering id for the jump bundle (same id as roster/sales; ' +
          'offering must use refundCalculationMode per_unit_count). Set “Jump bundle denominator” to match includedCount (usually 4).'
      );
      return;
    }
    let idToken = '';
    if (needsFuryToken) {
      const tok = await getFuryRegistrationIdToken();
      if (!tok.ok) {
        setError(tok.error || 'Could not get Fury ID token.');
        return;
      }
      idToken = tok.idToken;
    }
    const confirmed = window.confirm(
      'Declare jumping done?\n\n' +
        'This records each freshling’s jump count from the live manifest and saves completion as jumps ÷ bundle denominator (not capped; e.g. 5 jumps with denominator 4 = 1.25). ' +
        (needsFuryToken
          ? 'For Fury API events, Event Management must list the jump bundle Fury offering ID and denominator; FM POSTs jump-completion to Fury for each flyer with a Fury registration id (optional participation unit id on the flyer when Fury reports ambiguous lines).'
          : 'Fury writeback is skipped for sheet-based events; only Fresh Meet flyer fields are updated.')
    );
    if (!confirmed) return;
    setDeclaringJumpDone(true);
    setError('');
    try {
      const fn = httpsCallable(functions, 'declareManifestJumpingDone');
      const payload = { eventId: selectedEventId };
      if (idToken) payload.idToken = idToken;
      const res = await fn(payload);
      const data = res.data;
      const s = data?.summary;
      const parts = [
        `Freshling docs scanned: ${s?.freshlingDocs ?? '?'}.`,
        needsFuryToken
          ? `Fury: ${s?.furySucceeded ?? 0} ok, ${s?.furyFailed ?? 0} failed, ${s?.furySkippedNoRegistrationId ?? 0} missing Fury registration id, ${s?.furySkippedNoActiveLine ?? 0} no active jump bundle line.`
          : 'Fury writeback skipped (not a Fury API event).',
      ];
      if (s?.skippedTest) parts.push(`Test flyers skipped: ${s.skippedTest}.`);
      window.alert(parts.join('\n'));
      if (data?.errorSamples?.length) {
        const shortenFurySampleError = (err) => {
          const t = String(err || '');
          if (t.includes('<!DOCTYPE') || t.includes('<title>') || t.includes('Cannot POST')) {
            const m = t.match(/Cannot POST\s+\S+/);
            if (m) {
              return `${m[0]} — HTML from server (wrong FURY_REGISTRATION_API_BASE, often localhost:3000, or Fury route missing). See docs/fury-jump-completion-declare-flow.md#troubleshooting.`;
            }
            return 'HTML error from server — check FURY_REGISTRATION_API_BASE and Fury jump-completion route. See docs/fury-jump-completion-declare-flow.md#troubleshooting.';
          }
          if ((t.includes('Request URL:') || t.includes(' URL: ')) && !t.includes('<')) {
            const m = t.match(/\b(Request URL:|URL:)\s+(https?:\/\/\S+)/);
            if (m) {
              const fullUrl = m[2];
              const head = t.slice(0, m.index).trim();
              const headShort = head.length > 700 ? `${head.slice(0, 700)}…` : head;
              return `${headShort}\n\n${m[1]} ${fullUrl}`;
            }
            return t.length > 1200 ? `${t.slice(0, 1200)}…` : t;
          }
          return t.length > 480 ? `${t.slice(0, 480)}…` : t;
        };
        window.alert(
          'Sample errors:\n' +
            data.errorSamples
              .slice(0, 8)
              .map((row) => {
                const who = row.email || row.registrationId || '?';
                const errBody = shortenFurySampleError(row.error);
                const ru = (row.requestUrl || '').toString().trim();
                const extraUrl =
                  ru && !errBody.includes(ru) ? `\nRequest URL: ${ru}` : '';
                return `${who}: ${errBody}${extraUrl}`;
              })
              .join('\n')
        );
      }
      const flyerSnap = await getDocs(
        query(collection(firestore, 'flyers'), where('eventId', '==', selectedEventId))
      );
      setFlyers(flyerSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setError(e.message || 'Declare jumping done failed');
    } finally {
      setDeclaringJumpDone(false);
    }
  };

  const updateSlotParticipants = async (slot, newEmails) => {
    if (!manifest || !selectedEventId) return;
    const idx = (manifest.slots || []).findIndex((s) => slotsMatchSameCell(s, slot));
    if (idx < 0) {
      setError('Could not find this slot to update (try refreshing).');
      return;
    }
    const nextSlots = [...(manifest.slots || [])];
    nextSlots[idx] = {
      ...nextSlots[idx],
      participantEmails: newEmails,
    };
    try {
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, slots: nextSlots }),
        { merge: true }
      );
      setError('');
      setManifest((prev) => {
        if (!prev) return null;
        const slots = [...(prev.slots || [])];
        const i = slots.findIndex((s) => slotsMatchSameCell(s, slot));
        if (i < 0) return { ...prev, slots: nextSlots };
        slots[i] = { ...slots[i], participantEmails: newEmails };
        return { ...prev, slots };
      });
    } catch (e) {
      setError(e.message || 'Failed to update slot');
    }
  };

  const handleRemoveParticipant = (slot, email) => {
    if (!manifest || !selectedEventId) return;
    flushPendingDestructive();
    const idx = (manifest.slots || []).findIndex((s) => slotsMatchSameCell(s, slot));
    if (idx < 0) return;
    const normalized = (email || '').toLowerCase().trim();
    const prevEmails = [...(manifest.slots[idx].participantEmails || [])];
    const nextEmails = prevEmails.filter((e) => (e || '').toLowerCase().trim() !== normalized);
    const slotRef = { ...manifest.slots[idx] };
    const nextSlots = [...manifest.slots];
    nextSlots[idx] = { ...nextSlots[idx], participantEmails: nextEmails };
    const planeLbl = planes.find((pl) => pl.id === slotRef.planeId)?.label || slotRef.planeId;
    const loadLabel = getLoadLabelForCycle(
      slotRef.planeId,
      slotRef.loadIndex,
      selectedRound,
      manifest.loadMetadata || {},
      planes,
      manifest
    );
    const f = flyersByEmail.get(normalized);
    const personLabel = f ? getDisplayName(f) || email : email;
    setManifest((prev) => (prev ? { ...prev, slots: nextSlots } : null));
    const intendedEmails = nextEmails;
    const commit = () => {
      const m = manifestRef.current;
      if (!m || !selectedEventId) return;
      const slotsNow = [...(m.slots || [])];
      const i = slotsNow.findIndex((s) => slotsMatchSameCell(s, slotRef));
      if (i < 0) return;
      slotsNow[i] = { ...slotsNow[i], participantEmails: intendedEmails };
      setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({ ...m, slots: slotsNow }), {
        merge: true,
      })
        .then(() => setManifest((prev) => (prev ? { ...prev, slots: slotsNow } : null)))
        .catch((e) => setError(e.message || 'Failed to remove participant'));
    };
    const revert = () => {
      setManifest((prev) => {
        if (!prev) return null;
        const slotsNow = [...(prev.slots || [])];
        const i = slotsNow.findIndex((s) => slotsMatchSameCell(s, slotRef));
        if (i < 0) return prev;
        slotsNow[i] = { ...slotsNow[i], participantEmails: prevEmails };
        return { ...prev, slots: slotsNow };
      });
    };
    const timerId = setTimeout(() => {
      commit();
      setDestructiveUndo(null);
    }, 5000);
    setDestructiveUndo({
      timerId,
      message: `${personLabel} removed from ${loadLabel}, ${planeLbl} — Undo (5s)`,
      commit,
      revert: () => {
        clearTimeout(timerId);
        revert();
        setDestructiveUndo(null);
      },
    });
  };

  const otherJumps = manifest?.otherJumps || [];

  /** Persists load name for the currently selected manifest round only (see loadMetadata labelsByRound). */
  const saveLoadLabelForVisibleRound = async (planeId, loadIndex, text) => {
    if (!manifest || !selectedEventId) return;
    const key = `${planeId}:${loadIndex}`;
    const cycleKey = String(selectedRound);
    const trimmed = (text || '').trim();
    const prev = (manifest.loadMetadata || {})[key] || {};
    const lr = { ...(prev.labelsByRound || {}) };
    if (trimmed) lr[cycleKey] = trimmed;
    else delete lr[cycleKey];
    const nextEntry = stripUndefined({
      ...prev,
      labelsByRound: Object.keys(lr).length ? lr : undefined,
    });
    const nextAll = { ...(manifest.loadMetadata || {}) };
    if (nextEntry && Object.keys(nextEntry).length > 0) nextAll[key] = nextEntry;
    else delete nextAll[key];
    try {
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, loadMetadata: nextAll }),
        { merge: true }
      );
      setManifest((prev) => (prev ? { ...prev, loadMetadata: nextAll } : null));
    } catch (e) {
      setError(e.message || 'Failed to update load label');
    }
  };

  const populateLoadLabelsFrom = async (planeId, anchorLoadIndex, labelOverride, anchorRoundOpt) => {
    if (!manifest || !selectedEventId) return;
    const anchorRound = anchorRoundOpt != null ? Number(anchorRoundOpt) : selectedRound;
    const metaKey = `${planeId}:${anchorLoadIndex}`;
    const label = (
      labelOverride != null
        ? String(labelOverride)
        : getLoadLabelForCycle(
            planeId,
            anchorLoadIndex,
            anchorRound,
            manifest.loadMetadata || {},
            planes,
            manifest
          ) || ''
    ).trim();
    const parsed = parseLoadLabel(label);
    if (!parsed) return;
    const slots = manifest.slots || [];
    const cycles = manifest.cycles;
    const after = cellsAfterAnchor(planeId, anchorRound, anchorLoadIndex, slots, cycles);
    const hasOverride = labelOverride != null && String(labelOverride).trim() !== '';
    if (!hasOverride && after.length === 0) return;
    const updates = { ...(manifest.loadMetadata || {}) };
    const setLabelForRound = (key, roundStr, text) => {
      const prev = updates[key] || {};
      const lr = { ...(prev.labelsByRound || {}), [roundStr]: String(text).trim() };
      updates[key] = stripUndefined({ ...prev, labelsByRound: lr });
    };
    if (hasOverride) {
      setLabelForRound(metaKey, String(anchorRound), String(labelOverride).trim());
    }
    after.forEach((cell, i) => {
      setLabelForRound(
        `${planeId}:${cell.loadIndex}`,
        String(cell.round),
        `${parsed.name} ${parsed.number + i + 1}`
      );
    });
    const touchedKeys = new Set([`${planeId}:${anchorLoadIndex}`]);
    after.forEach((c) => touchedKeys.add(`${planeId}:${c.loadIndex}`));
    try {
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, loadMetadata: updates }),
        { merge: true }
      );
      setManifest((prev) => (prev ? { ...prev, loadMetadata: updates } : null));
      setEditingLoadLabel((prev) => {
        const next = { ...prev };
        touchedKeys.forEach((k) => delete next[k]);
        return next;
      });
    } catch (e) {
      setError(e.message || 'Failed to populate load labels');
    }
  };

  const addOtherJump = async () => {
    if (!manifest || !selectedEventId) return;
    const jumps = [...(manifest.otherJumps || []), { label: '', participantEmails: [] }];
    try {
      await setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({ ...manifest, otherJumps: jumps }), { merge: true });
      setManifest((prev) => (prev ? { ...prev, otherJumps: jumps } : null));
    } catch (e) {
      setError(e.message || 'Failed to add jump');
    }
  };

  const updateOtherJump = async (index, updates) => {
    if (!manifest || !selectedEventId) return;
    const jumps = [...(manifest.otherJumps || [])];
    if (index < 0 || index >= jumps.length) return;
    jumps[index] = { ...jumps[index], ...updates };
    try {
      await setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({ ...manifest, otherJumps: jumps }), { merge: true });
      setManifest((prev) => (prev ? { ...prev, otherJumps: jumps } : null));
    } catch (e) {
      setError(e.message || 'Failed to update jump');
    }
  };

  const removeOtherJump = async (index) => {
    if (!manifest || !selectedEventId) return;
    const jumps = (manifest.otherJumps || []).filter((_, i) => i !== index);
    try {
      await setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({ ...manifest, otherJumps: jumps }), { merge: true });
      setManifest((prev) => (prev ? { ...prev, otherJumps: jumps } : null));
    } catch (e) {
      setError(e.message || 'Failed to remove jump');
    }
  };

  const handleDropOnOtherJump = (jumpIndex, sourceSlot, type, email) => {
    if (!manifest || !selectedEventId) return;
    const jump = (manifest.otherJumps || [])[jumpIndex];
    if (!jump) return;
    let toAdd = [];
    if (type === 'participant' && email) {
      const normalized = (email || '').toLowerCase().trim();
      if (!normalized) return;
      const current = (jump.participantEmails || []).map((e) => (e || '').toLowerCase());
      if (current.includes(normalized)) return;
      const flyer = flyersByEmail.get(normalized);
      toAdd = [flyer ? (flyer.email || normalized) : normalized];
    } else if (type === 'team' && sourceSlot) {
      toAdd = [...(sourceSlot.participantEmails || [])];
    }
    if (!toAdd.length) return;
    const newJumpEmails = [...new Set([...(jump.participantEmails || []), ...toAdd])];
    if (type === 'participant') {
      const slots = manifest.slots.map((s) => {
        if (s.planeId !== sourceSlot.planeId || s.loadIndex !== sourceSlot.loadIndex ||
            s.slotIndexInLoad !== sourceSlot.slotIndexInLoad || s.cycleIndex !== sourceSlot.cycleIndex) return s;
        return { ...s, participantEmails: (s.participantEmails || []).filter(
          (e) => (e || '').toLowerCase() !== (email || '').toLowerCase()
        ) };
      });
      setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({
        ...manifest,
        slots,
        otherJumps: manifest.otherJumps.map((j, i) =>
          i === jumpIndex ? { ...j, participantEmails: newJumpEmails } : j
        ),
      }), { merge: true }).then(() => setManifest((prev) => {
        if (!prev) return null;
        const nextSlots = prev.slots.map((s) => {
          if (s.planeId !== sourceSlot.planeId || s.loadIndex !== sourceSlot.loadIndex ||
              s.slotIndexInLoad !== sourceSlot.slotIndexInLoad || s.cycleIndex !== sourceSlot.cycleIndex) return s;
          return { ...s, participantEmails: (s.participantEmails || []).filter(
            (e) => (e || '').toLowerCase() !== (email || '').toLowerCase()
          ) };
        });
        const nextJumps = prev.otherJumps.map((j, i) =>
          i === jumpIndex ? { ...j, participantEmails: newJumpEmails } : j
        );
        return { ...prev, slots: nextSlots, otherJumps: nextJumps };
      })).catch((e) => setError(e.message || 'Failed to move'));
    } else {
      const teamId = sourceSlot.teamId;
      const teamNumber = sourceSlot.teamNumber;
      const slots = manifest.slots.map((s) =>
        s.teamId === teamId && numEq(s.cycleIndex, sourceSlot.cycleIndex)
          ? { ...s, participantEmails: [] }
          : s
      );
      const updatedJump = {
        ...manifest.otherJumps[jumpIndex],
        participantEmails: newJumpEmails,
        teamId,
        teamNumber,
      };
      setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({
        ...manifest,
        slots,
        otherJumps: manifest.otherJumps.map((j, i) =>
          i === jumpIndex ? updatedJump : j
        ),
      }), { merge: true }).then(() => setManifest((prev) => {
        if (!prev) return null;
        const nextSlots = prev.slots.map((s) =>
          s.teamId === teamId && numEq(s.cycleIndex, sourceSlot.cycleIndex)
            ? { ...s, participantEmails: [] }
            : s
        );
        const nextJumps = prev.otherJumps.map((j, i) =>
          i === jumpIndex ? updatedJump : j
        );
        return { ...prev, slots: nextSlots, otherJumps: nextJumps };
      })).catch((e) => setError(e.message || 'Failed to move'));
    }
  };

  const handleAddParticipant = (slot, email) => {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized) return;
    const current = (slot.participantEmails || []).map((e) => (e || '').toLowerCase());
    if (current.includes(normalized)) return;
    const flyer = flyersByEmail.get(normalized);
    const displayEmail = flyer ? (flyer.email || normalized) : normalized;
    updateSlotParticipants(slot, [...(slot.participantEmails || []), displayEmail]);
  };

  const handleAddParticipantByScope = async (slot, email, scope = 'load') => {
    const normalized = (email || '').trim().toLowerCase();
    if (!normalized || !manifest || !selectedEventId) return;
    if (scope === 'load') {
      handleAddParticipant(slot, email);
      return;
    }
    try {
      const teamId = String(slot?.teamId || '');
      const slots = [...(manifest.slots || [])];
      const flyer = flyersByEmail.get(normalized);
      const displayEmail = flyer ? (flyer.email || normalized) : normalized;
      const nextSlots = slots.map((s) => {
        const sameTeam = teamId && String(s.teamId || '') === teamId;
        const sameCell = !teamId && slotsMatchSameCell(s, slot);
        if (!sameTeam && !sameCell) return s;
        if (!matchesRoundScope(s.cycleIndex, scope)) return s;
        const current = (s.participantEmails || []).map((e) => (e || '').toLowerCase().trim());
        if (current.includes(normalized)) return s;
        return { ...s, participantEmails: [...(s.participantEmails || []), displayEmail] };
      });
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, slots: nextSlots }),
        { merge: true }
      );
      setManifest((prev) => (prev ? { ...prev, slots: nextSlots } : null));
    } catch (e) {
      setError(e.message || 'Failed to add participant');
    }
  };

  const handleRemoveParticipantByScope = async (slot, email, scope = 'forward') => {
    const normalized = (email || '').toLowerCase().trim();
    if (!normalized || !manifest || !selectedEventId) return;
    const normalizedScope = scope === 'single' ? 'load' : scope;
    if (normalizedScope === 'load') {
      handleRemoveParticipant(slot, email);
      return;
    }
    try {
      const teamId = String(slot?.teamId || '');
      const slots = [...(manifest.slots || [])];
      const nextSlots = slots.map((s) => {
        const sameTeam = teamId && String(s.teamId || '') === teamId;
        const sameCell = !teamId && slotsMatchSameCell(s, slot);
        if (!sameTeam && !sameCell) return s;
        if (!matchesRoundScope(s.cycleIndex, normalizedScope)) return s;
        return {
          ...s,
          participantEmails: (s.participantEmails || []).filter(
            (e) => (e || '').toLowerCase().trim() !== normalized
          ),
        };
      });
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, slots: nextSlots }),
        { merge: true }
      );
      setManifest((prev) => (prev ? { ...prev, slots: nextSlots } : null));
    } catch (e) {
      setError(e.message || 'Failed to remove participant');
    }
  };

  const handleOffloadAllParticipantsByScope = async (slot, scope = 'forward') => {
    if (!manifest || !selectedEventId) return;
    const normalizedScope = scope === 'single' ? 'load' : scope;
    if (normalizedScope === 'load') {
      updateSlotParticipants(slot, []);
      return;
    }
    try {
      const teamId = String(slot?.teamId || '');
      const slots = [...(manifest.slots || [])];
      const nextSlots = slots.map((s) => {
        const sameTeam = teamId && String(s.teamId || '') === teamId;
        const sameCell = !teamId && slotsMatchSameCell(s, slot);
        if (!sameTeam && !sameCell) return s;
        if (!matchesRoundScope(s.cycleIndex, normalizedScope)) return s;
        return { ...s, participantEmails: [] };
      });
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, slots: nextSlots }),
        { merge: true }
      );
      setManifest((prev) => (prev ? { ...prev, slots: nextSlots } : null));
    } catch (e) {
      setError(e.message || 'Failed to off-load participants');
    }
  };

  const findManifestSlotWithParticipantInCycle = (emailRaw, cycleIndex) => {
    const normalized = (emailRaw || '').toLowerCase().trim();
    if (!normalized) return null;
    return (manifest?.slots || []).find(
      (s) =>
        numEq(s.cycleIndex, cycleIndex) &&
        (s.participantEmails || []).some((e) => (e || '').toLowerCase().trim() === normalized)
    ) || null;
  };

  const findTeamSlotOnLoadForRound = (planeId, loadIndex, teamId, cycleIndex) => {
    if (!teamId) return null;
    const tid = String(teamId);
    return (manifest?.slots || []).find(
      (s) =>
        String(s.planeId ?? '') === String(planeId ?? '') &&
        numEq(s.loadIndex, loadIndex) &&
        s.teamId != null &&
        String(s.teamId) === tid &&
        numEq(s.cycleIndex, cycleIndex)
    ) || null;
  };

  /** Tracker / off-round person onto a manifest slot; if they are already on this round, move instead of duplicating. */
  const handleAssignOffPlanePersonToSlot = (targetSlot, person) => {
    if (!targetSlot || !person?.email || !manifest || !selectedEventId) return;
    const cycleIndex = Number(targetSlot.cycleIndex) || Number(selectedRound) || 1;
    const sourceSlot = findManifestSlotWithParticipantInCycle(person.email, cycleIndex);
    if (sourceSlot) {
      handleParticipantDrop(targetSlot, sourceSlot, person.email);
      return;
    }
    handleAddParticipant(targetSlot, person.email);
  };

  /** Off-plane person onto an empty grid cell: place team + that person only (subset of roster). */
  const handleAssignOffPlanePersonToEmptySlot = async (planeId, loadIndex, slotIndexInLoad, person) => {
    if (!person?.email || !person.teamId || !manifest || !selectedEventId) return;
    const team = teams.find((t) => String(t.id) === String(person.teamId));
    if (!team) return;
    const normalized = (person.email || '').toLowerCase().trim();
    const flyer = flyersByEmail.get(normalized);
    const displayEmail = flyer ? (flyer.email || person.email) : person.email;
    const sourceSlot = findManifestSlotWithParticipantInCycle(person.email, selectedRound);
    if (sourceSlot) {
      if (sameManifestSlotPosition(sourceSlot, planeId, loadIndex, slotIndexInLoad)) return;
      if (isManifestSlotOccupiedForRound(planeId, loadIndex, slotIndexInLoad, selectedRound)) {
        const targetSlot = lastSlotForCellAndCycle(
          manifest.slots,
          planeId,
          loadIndex,
          slotIndexInLoad,
          selectedRound
        );
        if (targetSlot) {
          handleParticipantDrop(targetSlot, sourceSlot, person.email);
        }
        return;
      }
      const slots = (manifest.slots || []).map((s) => {
        if (
          s.planeId === sourceSlot.planeId &&
          numEq(s.loadIndex, sourceSlot.loadIndex) &&
          numEq(s.slotIndexInLoad, sourceSlot.slotIndexInLoad) &&
          numEq(s.cycleIndex, sourceSlot.cycleIndex)
        ) {
          return {
            ...s,
            participantEmails: (s.participantEmails || []).filter(
              (e) => (e || '').toLowerCase().trim() !== normalized
            ),
          };
        }
        return s;
      });
      slots.push(
        stripUndefined({
          planeId,
          loadIndex,
          slotIndexInLoad,
          cycleIndex: selectedRound,
          teamId: team.id,
          teamNumber: (team.teamNumber || '').toString(),
          participantEmails: [displayEmail],
        })
      );
      try {
        await setDoc(
          doc(firestore, 'eventManifest', selectedEventId),
          stripUndefined({ ...manifest, slots }),
          { merge: true }
        );
        setManifest((prev) => (prev ? { ...prev, slots } : null));
      } catch (e) {
        setError(e.message || 'Failed to move participant');
      }
      return;
    }
    await handleAssignTeamToEmptySlot(planeId, loadIndex, slotIndexInLoad, team, {
      participantEmailsOverride: [displayEmail],
    });
  };

  const addEmailsToOtherJump = async (jumpIndex, emailsToAdd, teamMeta) => {
    if (!manifest || !selectedEventId) return;
    const jump = (manifest.otherJumps || [])[jumpIndex];
    if (!jump || !emailsToAdd?.length) return;
    const lower = new Set((jump.participantEmails || []).map((e) => (e || '').toLowerCase().trim()));
    const merged = [...(jump.participantEmails || [])];
    for (const e of emailsToAdd) {
      const raw = (e || '').trim();
      if (!raw) continue;
      const k = raw.toLowerCase();
      if (lower.has(k)) continue;
      lower.add(k);
      merged.push(raw);
    }
    const updates = { participantEmails: merged };
    if (teamMeta?.teamId != null) updates.teamId = teamMeta.teamId;
    if (teamMeta?.teamNumber != null) updates.teamNumber = teamMeta.teamNumber;
    await updateOtherJump(jumpIndex, updates);
  };

  const jumpToManifestedPerson = (emailKey, preferredRound) => {
    const key = (emailKey || '').toLowerCase().trim();
    if (!key) return false;
    const slots = manifest?.slots || [];
    const sorted = [...slots].sort((a, b) => {
      const ar = Number(a.cycleIndex) || 0;
      const br = Number(b.cycleIndex) || 0;
      if (preferredRound && ar === preferredRound && br !== preferredRound) return -1;
      if (preferredRound && br === preferredRound && ar !== preferredRound) return 1;
      if (ar !== br) return ar - br;
      if ((Number(a.loadIndex) || 0) !== (Number(b.loadIndex) || 0)) return (Number(a.loadIndex) || 0) - (Number(b.loadIndex) || 0);
      return (Number(a.slotIndexInLoad) || 0) - (Number(b.slotIndexInLoad) || 0);
    });
    const target = sorted.find((s) =>
      (s.participantEmails || []).some((e) => (e || '').toLowerCase().trim() === key)
    );
    if (!target) return false;
    const slotKey = `${target.planeId}:${target.loadIndex}:${target.slotIndexInLoad}:${target.cycleIndex}`;
    setDispatchViewMode('dispatch');
    setSelectedRound(Number(target.cycleIndex) || preferredRound || 1);
    setDispatchHeaderExpanded(true);
    setHighlightedEmailKey(key);
    setHighlightedSlotKey(slotKey);
    setPendingJumpTarget({
      round: Number(target.cycleIndex) || preferredRound || 1,
      planeId: target.planeId,
      loadIndex: Number(target.loadIndex) || 0,
      slotIndexInLoad: Number(target.slotIndexInLoad) || 0,
    });
    return true;
  };

  const handleDispatcherSearchSubmit = () => {
    const q = (dispatcherSearch || '').toLowerCase().trim();
    if (!q) return;
    if (!dispatcherSearchMatches.length) {
      window.alert('No matching person or team found.');
      return;
    }
    const i = Math.min(Math.max(0, dispatcherHighlightIndex), dispatcherSearchMatches.length - 1);
    const m = dispatcherSearchMatches[i];
    setDispatcherSelectedEmailKey(m.emailKey);
    setDispatcherCardEmailKey(m.emailKey);
    setDispatcherDropdownOpen(false);
    jumpToManifestedPerson(m.emailKey, selectedRound);
  };

  const handleParticipantDrop = (targetSlot, sourceSlot, email) => {
    if (!manifest || !selectedEventId || !targetSlot || !sourceSlot || !email) return;
    const targetKey = `${targetSlot.planeId}:${targetSlot.loadIndex}:${targetSlot.slotIndexInLoad}:${targetSlot.cycleIndex}`;
    const sourceKey = `${sourceSlot.planeId}:${sourceSlot.loadIndex}:${sourceSlot.slotIndexInLoad}:${sourceSlot.cycleIndex}`;
    if (targetKey === sourceKey) return;
    const normalized = (email || '').toLowerCase().trim();
    const targetEmails = (targetSlot.participantEmails || []).map((e) => (e || '').toLowerCase());
    if (targetEmails.includes(normalized)) return; // already there
    const flyer = flyersByEmail.get(normalized);
    const displayEmail = flyer ? (flyer.email || normalized) : normalized;
    const slots = manifest.slots.map((s) => {
      if (s.planeId === sourceSlot.planeId && s.loadIndex === sourceSlot.loadIndex &&
          s.slotIndexInLoad === sourceSlot.slotIndexInLoad && s.cycleIndex === sourceSlot.cycleIndex) {
        return { ...s, participantEmails: (s.participantEmails || []).filter(
          (e) => (e || '').toLowerCase() !== normalized
        ) };
      }
      if (s.planeId === targetSlot.planeId && s.loadIndex === targetSlot.loadIndex &&
          s.slotIndexInLoad === targetSlot.slotIndexInLoad && s.cycleIndex === targetSlot.cycleIndex) {
        return { ...s, participantEmails: [...(s.participantEmails || []), displayEmail] };
      }
      return s;
    });
    setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({ ...manifest, slots }), { merge: true })
      .then(() => setManifest((prev) => (prev ? { ...prev, slots } : null)))
      .catch((e) => setError(e.message || 'Failed to move participant'));
  };

  const handleTeamDrop = (targetSlotOrPos, sourceSlot) => {
    if (!sourceSlot) return;
    if (targetSlotOrPos?.planeId && targetSlotOrPos?.teamId) {
      const targetSlot = targetSlotOrPos;
      const targetKey = `${targetSlot.planeId}:${targetSlot.loadIndex}:${targetSlot.slotIndexInLoad}:${targetSlot.cycleIndex}`;
      const sourceKey = `${sourceSlot.planeId}:${sourceSlot.loadIndex}:${sourceSlot.slotIndexInLoad}:${sourceSlot.cycleIndex}`;
      if (targetKey === sourceKey) return;
      swapSlotsFull(sourceSlot, targetSlot);
    } else {
      const { planeId, loadIndex, slotIndexInLoad } = targetSlotOrPos;
      if (!planeId || !loadIndex || !slotIndexInLoad) return;
      const samePos = sourceSlot.planeId === planeId && sourceSlot.loadIndex === loadIndex && sourceSlot.slotIndexInLoad === slotIndexInLoad;
      if (samePos) return;
      moveTeamToEmptyPosition(sourceSlot, planeId, loadIndex, slotIndexInLoad);
    }
  };

  const swapSlotsFull = (slotA, slotB) => {
    if (!manifest || !selectedEventId) return;
    const slots = [...(manifest.slots || [])];
    const idxA = slots.findIndex(
      (s) =>
        s.planeId === slotA.planeId &&
        s.loadIndex === slotA.loadIndex &&
        s.slotIndexInLoad === slotA.slotIndexInLoad &&
        s.cycleIndex === slotA.cycleIndex
    );
    const idxB = slots.findIndex(
      (s) =>
        s.planeId === slotB.planeId &&
        s.loadIndex === slotB.loadIndex &&
        s.slotIndexInLoad === slotB.slotIndexInLoad &&
        s.cycleIndex === slotB.cycleIndex
    );
    if (idxA < 0 || idxB < 0) return;
    const a = slots[idxA];
    const b = slots[idxB];
    slots[idxA] = { ...a, teamId: b.teamId, teamNumber: b.teamNumber, participantEmails: b.participantEmails || [] };
    slots[idxB] = { ...b, teamId: a.teamId, teamNumber: a.teamNumber, participantEmails: a.participantEmails || [] };
    setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({ ...manifest, slots }), { merge: true })
      .then(() => setManifest((prev) => (prev ? { ...prev, slots } : null)))
      .catch((e) => setError(e.message || 'Failed to swap'));
  };

  const moveTeamToEmptyPosition = (sourceSlot, planeId, loadIndex, slotIndexInLoad) => {
    if (!manifest || !selectedEventId || !sourceSlot?.teamId) return;
    const slots = manifest.slots.map((s) => {
      if (s.teamId !== sourceSlot.teamId || !numEq(s.cycleIndex, sourceSlot.cycleIndex)) return s;
      return { ...s, planeId, loadIndex, slotIndexInLoad };
    });
    setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({ ...manifest, slots }), { merge: true })
      .then(() => setManifest((prev) => (prev ? { ...prev, slots } : null)))
      .catch((e) => setError(e.message || 'Failed to move team'));
  };

  /** Occupied when the canonical (last) row for this cell + round has a team — see lastSlotForCellAndCycle. */
  const isManifestSlotOccupiedForRound = (planeId, loadIndex, slotIndexInLoad, cycleIndex) =>
    Boolean(
      lastSlotForCellAndCycle(manifest?.slots, planeId, loadIndex, slotIndexInLoad, cycleIndex)?.teamId
    );

  const handleAssignTeamToEmptySlot = async (planeId, loadIndex, slotIndexInLoad, team, options) => {
    if (!manifest || !selectedEventId || !team?.id) return;
    setError('');
    if (isManifestSlotOccupiedForRound(planeId, loadIndex, slotIndexInLoad, selectedRound)) return;
    let teamForEmails = team;
    try {
      const teamSnap = await getDoc(doc(firestore, 'teams', team.id));
      const teamData = teamSnap.data();
      if (teamData) {
        teamForEmails = { id: teamSnap.id, ...teamData };
      }
    } catch {
      /* use in-memory team */
    }
    const fromTeam = participantEmailsFromTeam(teamForEmails);
    const participantEmails =
      options?.participantEmailsOverride != null
        ? [...options.participantEmailsOverride].map((e) => (e || '').toString().trim()).filter(Boolean)
        : fromTeam;
    if (!participantEmails.length) return;
    const teamDocId = String(team.id);
    const alreadyOnManifest = (manifest.slots || []).some(
      (s) => s.teamId != null && String(s.teamId) === teamDocId
    );
    if (alreadyOnManifest) {
      let slots = [...(manifest.slots || [])];
      const teamRowsThisRound = slots.filter(
        (s) => String(s.teamId) === teamDocId && numEq(s.cycleIndex, selectedRound)
      );
      if (teamRowsThisRound.length > 0) {
        // Move every manifest row for this team in this round (e.g. off-load tracker → another load).
        slots = slots.map((s) => {
          if (String(s.teamId) === teamDocId && numEq(s.cycleIndex, selectedRound)) {
            return stripUndefined({
              ...s,
              planeId,
              loadIndex,
              slotIndexInLoad,
              teamId: team.id,
              teamNumber: (team.teamNumber || '').toString(),
              participantEmails:
                participantEmails.length > 0 ? participantEmails : (s.participantEmails || []),
            });
          }
          return s;
        });
      } else {
        // Team exists on the manifest in other rounds only — add this round at the target cell.
        const atThisRound = slots.filter(
          (s) =>
            sameManifestSlotPosition(s, planeId, loadIndex, slotIndexInLoad) &&
            numEq(s.cycleIndex, selectedRound)
        );
        if (atThisRound.length > 0) {
          slots = slots.map((s) => {
            if (
              sameManifestSlotPosition(s, planeId, loadIndex, slotIndexInLoad) &&
              numEq(s.cycleIndex, selectedRound)
            ) {
              return stripUndefined({
                ...s,
                teamId: team.id,
                teamNumber: (team.teamNumber || '').toString(),
                participantEmails:
                  participantEmails.length > 0 ? participantEmails : (s.participantEmails || []),
              });
            }
            return s;
          });
        } else {
          slots.push(
            stripUndefined({
              planeId,
              loadIndex,
              slotIndexInLoad,
              cycleIndex: selectedRound,
              teamId: team.id,
              teamNumber: (team.teamNumber || '').toString(),
              participantEmails,
            })
          );
        }
      }
      try {
        await setDoc(
          doc(firestore, 'eventManifest', selectedEventId),
          stripUndefined({ ...manifest, slots }),
          { merge: true }
        );
        setManifest((prev) => (prev ? { ...prev, slots } : null));
      } catch (e) {
        setError(e.message || 'Failed to move team');
      }
      return;
    }
    const cycles = manifest.cycles || 4;
    let slots = [...(manifest.slots || [])];
    const atThisRound = slots.filter(
      (s) => sameManifestSlotPosition(s, planeId, loadIndex, slotIndexInLoad) && numEq(s.cycleIndex, selectedRound)
    );
    const atAnyRound = slots.filter((s) => sameManifestSlotPosition(s, planeId, loadIndex, slotIndexInLoad));
    if (atThisRound.length > 0) {
      slots = slots.map((s) => {
        if (sameManifestSlotPosition(s, planeId, loadIndex, slotIndexInLoad) && numEq(s.cycleIndex, selectedRound)) {
          return {
            ...s,
            teamId: team.id,
            teamNumber: (team.teamNumber || '').toString(),
            participantEmails,
          };
        }
        return s;
      });
    } else if (atAnyRound.length > 0) {
      slots.push({
        planeId,
        loadIndex,
        slotIndexInLoad,
        cycleIndex: selectedRound,
        teamId: team.id,
        teamNumber: (team.teamNumber || '').toString(),
        participantEmails,
      });
    } else {
      for (let cycleIndex = 1; cycleIndex <= cycles; cycleIndex++) {
        slots.push({
          planeId,
          loadIndex,
          slotIndexInLoad,
          cycleIndex,
          teamId: team.id,
          teamNumber: (team.teamNumber || '').toString(),
          participantEmails,
        });
      }
    }
    try {
      await setDoc(
        doc(firestore, 'eventManifest', selectedEventId),
        stripUndefined({ ...manifest, slots }),
        { merge: true }
      );
      setManifest((prev) => (prev ? { ...prev, slots } : null));
    } catch (e) {
      setError(e.message || 'Failed to assign team');
    }
  };

  const handleDropTeamFromOther = (state, targetSlotOrPos) => {
    if (!manifest || !selectedEventId) return;
    const { otherJumpIndex, teamId, participantEmails } = state;
    if (!teamId) return;
    let planeId, loadIndex, slotIndexInLoad;
    if (targetSlotOrPos?.planeId != null && targetSlotOrPos?.loadIndex != null && targetSlotOrPos?.slotIndexInLoad != null) {
      planeId = targetSlotOrPos.planeId;
      loadIndex = targetSlotOrPos.loadIndex;
      slotIndexInLoad = targetSlotOrPos.slotIndexInLoad;
    } else if (targetSlotOrPos?.planeId && targetSlotOrPos?.teamId) {
      const s = targetSlotOrPos;
      planeId = s.planeId;
      loadIndex = s.loadIndex;
      slotIndexInLoad = s.slotIndexInLoad;
    } else return;
    let slots = manifest.slots.map((s) => {
      if (s.teamId === teamId && numEq(s.cycleIndex, selectedRound)) {
        return { ...s, planeId, loadIndex, slotIndexInLoad, participantEmails: participantEmails || [] };
      }
      return s;
    });
    slots = slots.map((s) => {
      const atTarget =
        s.planeId === planeId &&
        s.loadIndex === loadIndex &&
        s.slotIndexInLoad === slotIndexInLoad &&
        numEq(s.cycleIndex, selectedRound);
      if (atTarget && s.teamId !== teamId) {
        const { teamId: _tid, teamNumber: _tnum, ...rest } = s;
        return { ...rest, participantEmails: [] };
      }
      return s;
    });
    const updatedJump = { ...manifest.otherJumps[otherJumpIndex] };
    delete updatedJump.teamId;
    delete updatedJump.teamNumber;
    updatedJump.participantEmails = (updatedJump.participantEmails || []).filter(
      (e) => !(participantEmails || []).some((pe) => (pe || '').toLowerCase() === (e || '').toLowerCase())
    );
    const otherJumps = manifest.otherJumps.map((j, i) => (i === otherJumpIndex ? updatedJump : j));
    setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({ ...manifest, slots, otherJumps }), { merge: true })
      .then(() => setManifest((prev) => (prev ? { ...prev, slots, otherJumps } : null)))
      .catch((e) => setError(e.message || 'Failed to move team back'));
  };

  /** First free team position on this plane+load for the visible round (planner Shark 1 / Gypsy 1 targets). */
  const getFirstEmptyTeamSlotOnLoad = (planeId, loadIndex) => {
    for (let si = 1; si <= teamsPerLoad; si++) {
      if (!isManifestSlotOccupiedForRound(planeId, loadIndex, si, selectedRound)) return si;
    }
    return null;
  };

  const handlePlannerLoadCellDrop = (planeId, loadIndex, ds) => {
    if (!ds || !manifest || !selectedEventId) return;
    setError('');
    let slotIdx = getFirstEmptyTeamSlotOnLoad(planeId, loadIndex);
    if (ds.type === 'offPlane' && ds.person?.email && slotIdx == null && ds.person.teamId) {
      const existing = findTeamSlotOnLoadForRound(planeId, loadIndex, ds.person.teamId, selectedRound);
      if (existing) {
        handleAssignOffPlanePersonToSlot(existing, ds.person);
        return;
      }
    }
    if (slotIdx == null) {
      setError('That load has no empty team slot.');
      return;
    }
    if (ds.type === 'offPlane' && ds.person?.email) {
      const slot = lastSlotForCellAndCycle(manifest.slots, planeId, loadIndex, slotIdx, selectedRound);
      if (slot) handleAssignOffPlanePersonToSlot(slot, ds.person);
      else void handleAssignOffPlanePersonToEmptySlot(planeId, loadIndex, slotIdx, ds.person);
    } else if (ds.type === 'offPlaneTeam' && ds.teamId) {
      const t = teams.find((tm) => String(tm.id) === String(ds.teamId));
      if (t) void handleAssignTeamToEmptySlot(planeId, loadIndex, slotIdx, t);
    } else if (ds.type === 'teamFromOther' && ds.teamId) {
      handleDropTeamFromOther(ds, { planeId, loadIndex, slotIndexInLoad: slotIdx });
    }
  };

  const clearSlotsByPredicate = async (predicate) => {
    if (!manifest || !selectedEventId) return;
    const slots = manifest.slots.map((s) =>
      predicate(s) ? { ...s, participantEmails: [] } : s
    );
    try {
      await setDoc(doc(firestore, 'eventManifest', selectedEventId), stripUndefined({
        ...manifest,
        slots,
      }), { merge: true });
      setManifest((prev) => (prev ? { ...prev, slots } : null));
    } catch (e) {
      setError(e.message || 'Failed to clear');
    }
  };

  const matchesRoundScope = (cycleIndex, scope) => {
    const round = Number(cycleIndex);
    const current = Number(selectedRound);
    if (scope === 'all') return true;
    if (scope === 'forward') return round >= current;
    return round === current;
  };

  const handleClearPlane = (planeId) => {
    if (!planeId || !window.confirm(`Clear all participants on ${planes.find((p) => p.id === planeId)?.label || planeId} across all 4 rounds?`)) return;
    clearSlotsByPredicate((s) => s.planeId === planeId);
  };

  const handlePrintPlaneOrder = () => {
    const slots = manifest?.slots || [];
    if (!slots.length) {
      window.alert('No manifest data to print.');
      return;
    }

    const planeIndex = new Map((planes || []).map((p, idx) => [p.id, idx]));
    const rows = slots
      .filter((s) => s.teamId)
      .slice()
      .sort((a, b) => {
        if (a.cycleIndex !== b.cycleIndex) return a.cycleIndex - b.cycleIndex;
        if (summaryLiftOrder === 'byPlane') {
          const ai = planeIndex.get(a.planeId) ?? 999;
          const bi = planeIndex.get(b.planeId) ?? 999;
          if (ai !== bi) return ai - bi;
          if (a.loadIndex !== b.loadIndex) return a.loadIndex - b.loadIndex;
          return a.slotIndexInLoad - b.slotIndexInLoad;
        }
        if (a.loadIndex !== b.loadIndex) return a.loadIndex - b.loadIndex;
        const ai = planeIndex.get(a.planeId) ?? 999;
        const bi = planeIndex.get(b.planeId) ?? 999;
        if (ai !== bi) return ai - bi;
        return a.slotIndexInLoad - b.slotIndexInLoad;
      })
      .map((slot) => {
        const team = teams.find((t) => String(t.id) === String(slot.teamId));
        const people = (slot.participantEmails || [])
          .map((email) => {
            const k = (email || '').toLowerCase().trim();
            const f = flyersByEmail.get(k);
            const role = participantRoleForSlot(slot, email, team, flyersByEmail);
            const type = role || String(f?.type || '').toLowerCase();
            return {
              type,
              name: f ? getDisplayName(f) || f.email || email : email,
            };
          })
          .filter((p) => p.name);
        const mentors = people.filter((p) => p.type === 'mentor').map((p) => p.name);
        const freshlings = people.filter((p) => p.type === 'freshling').map((p) => p.name);
        const others = people
          .filter((p) => p.type !== 'mentor' && p.type !== 'freshling')
          .map((p) => p.name);
        return {
          cycleIndex: slot.cycleIndex,
          loadIndex: slot.loadIndex,
          planeId: slot.planeId,
          planeLabel: planes.find((p) => p.id === slot.planeId)?.label || slot.planeId,
          teamNumber: slot.teamNumber || '?',
          mentorLabel: mentors.length ? mentors.join(', ') : '—',
          freshlingsLabel: [...freshlings, ...others].length ? [...freshlings, ...others].join(', ') : '—',
        };
      });

    if (!rows.length) {
      window.alert('No assigned teams to print.');
      return;
    }

    const esc = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const roundMap = new Map();
    rows.forEach((r) => {
      if (!roundMap.has(r.cycleIndex)) roundMap.set(r.cycleIndex, []);
      roundMap.get(r.cycleIndex).push(r);
    });
    const roundNumbers = Array.from(roundMap.keys()).sort((a, b) => a - b);
    const pagesHtml = roundNumbers
      .map((roundNum, idx) => {
        const roundRows = roundMap.get(roundNum) || [];
        const linesHtml = roundRows
          .map((r) => {
            const planeAltClass = ((planeIndex.get(r.planeId) ?? 0) % 2) === 1 ? 'edm-print-plane-alt' : 'edm-print-plane-base';
            return `<tr class="${planeAltClass}">
              <td class="edm-print-main">Team #${esc(r.teamNumber)}, ${esc(r.mentorLabel)}, ${esc(r.freshlingsLabel)}</td>
              <td class="edm-print-meta">${esc(r.planeLabel)} · Load ${esc(r.loadIndex)}</td>
            </tr>`;
          })
          .join('');
        return `<section class="edm-print-page${idx < roundNumbers.length - 1 ? ' edm-print-page-break' : ''}">
          <h2>Round ${esc(roundNum)}</h2>
          <table><tbody>${linesHtml}</tbody></table>
        </section>`;
      })
      .join('');

    const eventTitle = events.find((ev) => ev.id === selectedEventId)?.title || 'Event';
    const title = `${eventTitle} - Day Manifest Plane Order`;
    const css = `
      body { margin: 0.45in; font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #111827; }
      h1 { margin: 0 0 0.3rem 0; font-size: 24px; }
      h2 { margin: 0 0 0.4rem 0; font-size: 20px; }
      p { margin: 0 0 0.7rem 0; color: #4b5563; font-size: 15px; }
      table { width: 100%; border-collapse: collapse; font-size: 15px; }
      td { border: 1px solid #e5e7eb; padding: 9px 10px; vertical-align: top; }
      .edm-print-plane-base td { background: #ffffff; }
      .edm-print-plane-alt td { background: #eef2ff; }
      .edm-print-main { width: 78%; }
      .edm-print-meta { width: 22%; white-space: nowrap; color: #1f2937; font-weight: 600; }
      .edm-print-page { margin-bottom: 0.35in; }
      .edm-print-page-break { page-break-after: always; break-after: page; }
      @media print {
        body { margin: 0.4in; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        table, tr, td, .edm-print-plane-base td, .edm-print-plane-alt td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .edm-print-page-break { page-break-after: always; break-after: page; }
      }
    `;
    const orderNote =
      summaryLiftOrder === 'byPlane'
        ? 'Grouped by plane (all loads on plane 1, then plane 2, …), then load number within each plane.'
        : 'By load with alternating planes (load 1 plane A, load 1 plane B, load 2 …).';
    const bodyHtml = `<main><h1>${esc(title)}</h1><p>Order: team number, mentor, freshlings. ${orderNote} Each round starts on a new page.</p>${pagesHtml}</main>`;
    openPrintWindow(title, bodyHtml, css);
  };

  const handlePrintJumpCounts = () => {
    const countByEmail = buildManifestJumpCountByEmail(manifest);
    if (countByEmail.size === 0) {
      window.alert('No participants on the manifest to print.');
      return;
    }
    const displayEmailByLower = new Map();
    for (const slot of manifest?.slots || []) {
      for (const email of slot.participantEmails || []) {
        const key = (email || '').toLowerCase().trim();
        if (key && !displayEmailByLower.has(key)) displayEmailByLower.set(key, String(email).trim());
      }
    }
    for (const jump of manifest?.otherJumps || []) {
      for (const email of jump.participantEmails || []) {
        const key = (email || '').toLowerCase().trim();
        if (key && !displayEmailByLower.has(key)) displayEmailByLower.set(key, String(email).trim());
      }
    }
    const rows = Array.from(countByEmail.entries())
      .map(([key, jumps]) => {
        const f = flyersByEmail.get(key);
        const email = displayEmailByLower.get(key) || key;
        const name = f ? getDisplayName(f) || email : email;
        return { name, email, jumps };
      })
      .sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })
      );
    const esc = (s) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    const linesHtml = rows
      .map(
        (r) =>
          `<tr><td>${esc(r.name)}</td><td>${esc(r.email)}</td><td class="edm-print-num">${esc(r.jumps)}</td></tr>`
      )
      .join('');
    const eventTitle = events.find((ev) => ev.id === selectedEventId)?.title || 'Event';
    const title = `${eventTitle} - Jump counts (manifest)`;
    const css = `
      body { margin: 0.45in; font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; color: #111827; }
      h1 { margin: 0 0 0.35rem 0; font-size: 22px; }
      p { margin: 0 0 0.75rem 0; color: #4b5563; font-size: 14px; }
      table { width: 100%; border-collapse: collapse; font-size: 14px; }
      th, td { border: 1px solid #e5e7eb; padding: 8px 10px; text-align: left; }
      th { background: #f3f4f6; font-weight: 600; }
      .edm-print-num { text-align: right; width: 5rem; font-variant-numeric: tabular-nums; }
      @media print {
        body { margin: 0.4in; }
      }
    `;
    const bodyHtml = `<main><h1>${esc(title)}</h1><p>Each row is one person. Jumps = meet slots plus extra (off-grid) jumps.</p><table><thead><tr><th>Name</th><th>Email</th><th>Jumps</th></tr></thead><tbody>${linesHtml}</tbody></table></main>`;
    openPrintWindow(title, bodyHtml, css);
  };

  if (loading && !events.length) {
    return (
      <div className="event-day-manifest">
        <h2>Event Day Manifest</h2>
        <p>Loading…</p>
      </div>
    );
  }

  const resolveSlotTeamNumber = (slot) => {
    const direct = String(slot?.teamNumber ?? '').trim();
    if (direct) return direct;
    const match = teams.find((t) => String(t.id) === String(slot?.teamId));
    const fromTeamNumber = String(match?.teamNumber ?? '').trim();
    if (fromTeamNumber) return fromTeamNumber;
    const fromNumber = String(match?.number ?? '').trim();
    if (fromNumber) return fromNumber;
    return '?';
  };

  const getTeamForSlot = (slot) => teams.find((t) => String(t.id) === String(slot?.teamId));

  const getParticipantRoleForSlot = (slot, email) =>
    participantRoleForSlot(slot, email, getTeamForSlot(slot), flyersByEmail);

  const getSlotParticipantEmailsSorted = (slot) => {
    const emails = [...(slot?.participantEmails || [])];
    const roleRank = (em) => {
      const r = getParticipantRoleForSlot(slot, em);
      if (r === 'mentor') return 0;
      if (r === 'freshling') return 1;
      return 2;
    };
    return emails.sort((a, b) => {
      const ar = roleRank(a);
      const br = roleRank(b);
      if (ar !== br) return ar - br;
      const an = (getDisplayName(flyersByEmail.get((a || '').toLowerCase().trim())) || a || '').toLowerCase();
      const bn = (getDisplayName(flyersByEmail.get((b || '').toLowerCase().trim())) || b || '').toLowerCase();
      return an.localeCompare(bn, undefined, { sensitivity: 'base' });
    });
  };

  return (
    <div className="event-day-manifest">
      <div className="event-day-manifest-header">
        <h2>Event Day Manifest</h2>
        <div>
          <label>
            Event:{' '}
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              {events.map((ev) => (
                <option key={ev.id} value={ev.id}>
                  {ev.title}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      {error && <p className="event-day-manifest-error">{error}</p>}
      {furyDeclareOfferingMisconfigured && (
        <p
          className="event-day-manifest-error"
          style={{ background: '#fff8e6', border: '1px solid #e6c200' }}
          role="status"
        >
          <strong>Fury declare not configured.</strong> Open <strong>Event Management</strong>, edit this event, and
          under <strong>Registration import</strong> set <strong>Jump completion Fury offering ID</strong> (Fury
          catalog id for the jump bundle — same as roster/sales). Match <strong>Jump bundle denominator</strong> to
          that offering’s <code>includedCount</code> (usually 4).
        </p>
      )}
      <div className="event-day-manifest-toolbar event-day-manifest-print-toolbar">
        <button
          type="button"
          className="event-day-manifest-btn event-day-manifest-btn--primary btn-fm-primary"
          onClick={handlePopulate}
          disabled={populating || !selectedEventId}
        >
          {populating ? 'Populating…' : 'Populate from load order'}
        </button>
        <button
          type="button"
          className="event-day-manifest-btn event-day-manifest-btn--outline"
          onClick={handleDeclareJumpingDone}
          disabled={
            declaringJumpDone || !selectedEventId || !manifest || furyDeclareOfferingMisconfigured
          }
          title={
            furyDeclareOfferingMisconfigured
              ? 'Set Jump completion Fury offering ID on this event (Event Management → edit event → Registration import) before declaring.'
              : 'Finalize jump counts from this manifest (jumps ÷ bundle denominator, may exceed 1). ' +
                'For Fury API events, POSTs jump-completion to Fury (optional path override: FURY_REGISTRATION_JUMP_COMPLETION_TEMPLATE).'
          }
        >
          {declaringJumpDone ? 'Declaring…' : 'Declare jumping done'}
        </button>
        <div
          className="event-day-manifest-print-view-selector"
          role="group"
          aria-label="Print manifest outputs"
        >
          <button
            type="button"
            className={`event-day-manifest-print-view-btn${
              manifestPrintOutputView === 'planeOrder' ? ' event-day-manifest-print-view-btn--active' : ''
            }`}
            disabled={!manifest?.slots?.length || !selectedEventId}
            title="Teams grouped by load for pilots and ops flow."
            onClick={() => {
              setManifestPrintOutputView('planeOrder');
              handlePrintPlaneOrder();
            }}
          >
            Plane order
          </button>
          <button
            type="button"
            className={`event-day-manifest-print-view-btn${
              manifestPrintOutputView === 'jumpCounts' ? ' event-day-manifest-print-view-btn--active' : ''
            }`}
            disabled={manifestJumpCountByEmail.size === 0 || !selectedEventId}
            title="Per-person jump totals for DZ billing reference."
            onClick={() => {
              setManifestPrintOutputView('jumpCounts');
              handlePrintJumpCounts();
            }}
          >
            Jump counts
          </button>
          <button
            type="button"
            className={`event-day-manifest-print-view-btn${
              manifestPrintOutputView === 'planeTable' ? ' event-day-manifest-print-view-btn--active' : ''
            }`}
            disabled={!edmPlaneOrderExportRows.length || !selectedEventId}
            title="Formatted rounds-by-plane reconciliation table."
            onClick={() => openEdmExport('planeOrder', 'print')}
          >
            Plane table
          </button>
          <button
            type="button"
            className={`event-day-manifest-print-view-btn${
              manifestPrintOutputView === 'jumpTable' ? ' event-day-manifest-print-view-btn--active' : ''
            }`}
            disabled={!edmJumpCountExportRows.length || !selectedEventId}
            title="Per-person jump counts across rounds."
            onClick={() => openEdmExport('jumpCounts', 'print')}
          >
            Jump table
          </button>
          <button
            type="button"
            className={`event-day-manifest-print-view-btn${
              manifestPrintOutputView === 'jumpLog' ? ' event-day-manifest-print-view-btn--active' : ''
            }`}
            disabled={!rosterJumpLogRowsRaw.length || !selectedEventId}
            title="Detailed individual jump records for audit."
            onClick={() => openEdmExport('roster', 'print')}
          >
            Jump log
          </button>
        </div>
        <AdminCopyMenu
          ariaLabel="Copy day manifest tables"
          disabled={
            !selectedEventId ||
            (!edmPlaneOrderExportRows.length &&
              !edmJumpCountExportRows.length &&
              !rosterJumpLogRowsRaw.length)
          }
          items={[
            {
              label: 'Copy plane order table…',
              disabled: !edmPlaneOrderExportRows.length,
              onSelect: () => openEdmExport('planeOrder', 'copy'),
              showCopiedFeedback: false,
            },
            {
              label: 'Copy jump counts table…',
              disabled: !edmJumpCountExportRows.length,
              onSelect: () => openEdmExport('jumpCounts', 'copy'),
              showCopiedFeedback: false,
            },
            {
              label: 'Copy jump log (by person)…',
              disabled: !rosterJumpLogRowsRaw.length,
              onSelect: () => openEdmExport('roster', 'copy'),
              showCopiedFeedback: false,
            },
          ]}
        />
      </div>

      <div className="event-day-manifest-dispatch-search">
        <div
          className="event-day-manifest-dispatch-search-wrap"
          ref={dispatchSearchWrapRef}
          role="combobox"
          aria-expanded={dispatcherDropdownOpen}
          aria-controls="edm-dispatch-search-results"
          aria-haspopup="listbox"
        >
          <input
            type="search"
            value={dispatcherSearch}
            onChange={(e) => {
              setDispatcherSearch(e.target.value);
              setDispatcherDropdownOpen(true);
            }}
            onFocus={() => setDispatcherDropdownOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                if (!dispatcherSearchMatches.length) return;
                setDispatcherHighlightIndex((i) => Math.min(i + 1, dispatcherSearchMatches.length - 1));
                setDispatcherDropdownOpen(true);
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setDispatcherHighlightIndex((i) => Math.max(0, i - 1));
              } else if (e.key === 'Escape') {
                setDispatcherDropdownOpen(false);
              } else if (e.key === 'Enter') {
                e.preventDefault();
                handleDispatcherSearchSubmit();
              }
            }}
            className="event-day-manifest-dispatch-search-input"
            placeholder="Search person (name, email, team #)"
            aria-label="Search person or team"
            aria-autocomplete="list"
          />
          {dispatcherDropdownOpen && dispatcherSearchMatches.length > 0 && (
            <div
              className="event-day-manifest-dispatch-search-dropdown"
              id="edm-dispatch-search-results"
              role="listbox"
            >
              {dispatcherSearchMatches.map((m, idx) => (
                <button
                  key={m.emailKey}
                  type="button"
                  role="option"
                  aria-selected={idx === dispatcherHighlightIndex}
                  className={`manifest-search-result${idx === dispatcherHighlightIndex ? ' manifest-search-result--active' : ''}${m.nameAmbiguous ? ' manifest-search-result--ambiguous' : ''}`}
                  onMouseEnter={() => setDispatcherHighlightIndex(idx)}
                  onClick={() => {
                    setDispatcherSelectedEmailKey(m.emailKey);
                    setDispatcherCardEmailKey(m.emailKey);
                    setDispatcherDropdownOpen(false);
                    jumpToManifestedPerson(m.emailKey, selectedRound);
                  }}
                >
                  <span className="primary">
                    {m.name || m.email}
                    {m.teamNumber != null ? ` · Team #${m.teamNumber}` : ''}
                  </span>
                  <span className={`status ${m.searchStatus.kind === 'on' ? 'on-load' : m.searchStatus.kind === 'off' ? 'off-load' : 'unassigned'}`}>
                    {m.searchStatus.kind === 'on' ? '✓ ' : m.searchStatus.kind === 'off' ? '⚠ ' : ''}
                    {m.searchStatus.label}
                  </span>
                  {m.nameAmbiguous && (
                    <span className="secondary">{m.email}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      {dispatcherCardData && (
        <div className="event-day-manifest-dispatch-card event-day-manifest-dispatch-card--confirm">
          <div className="event-day-manifest-dispatch-card-title">
            {dispatcherCardData.name || dispatcherCardData.email}
            {dispatcherCardData.teamNumber != null ? ` · Team #${dispatcherCardData.teamNumber}` : ''}
          </div>
          {dispatcherCardData.cardStatus && (
            <p
              className={`event-day-manifest-dispatch-card-meta ${dispatcherCardData.cardStatus.kind === 'on' ? 'on-load' : dispatcherCardData.cardStatus.kind === 'off' ? 'off-load' : 'unassigned'}`}
            >
              {dispatcherCardData.cardStatus.kind === 'on' ? '✓ ' : dispatcherCardData.cardStatus.kind === 'off' ? '⚠ ' : ''}
              {dispatcherCardData.cardStatus.label}
            </p>
          )}
          <div className="event-day-manifest-dispatch-card-rounds">
            {dispatcherCardData.rounds.map((r) => (
              <div key={r.round} className="event-day-manifest-dispatch-card-round">
                <span>R{r.round}: {r.assignments.length ? `${r.assignments.length} load${r.assignments.length > 1 ? 's' : ''}` : 'off load'}</span>
                {r.assignments.length > 0 && (
                  <button
                    type="button"
                    className="event-day-manifest-btn event-day-manifest-btn--outline"
                    onClick={() => jumpToManifestedPerson(dispatcherCardData.emailKey, r.round)}
                  >
                    Jump to spot
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      

      {!manifest?.slots?.length ? (
        <p className="event-day-manifest-empty">
          No manifest data. Click &quot;Populate from load order&quot; to create it from the
          current load order (4 rounds).
        </p>
      ) : (
        <div className="event-day-manifest-workspace">
          <div className="event-day-manifest-main-tabs" role="tablist" aria-label="Manifest workspace mode">
            <button
              type="button"
              role="tab"
              className={`event-day-manifest-main-tab${
                dispatchViewMode === 'dispatch' ? ' event-day-manifest-main-tab--active' : ''
              }`}
              aria-selected={dispatchViewMode === 'dispatch'}
              onClick={() => setDispatchViewMode('dispatch')}
            >
              Dispatch
            </button>
            <button
              type="button"
              role="tab"
              className={`event-day-manifest-main-tab${
                dispatchViewMode === 'audit' ? ' event-day-manifest-main-tab--active' : ''
              }`}
              aria-selected={dispatchViewMode === 'audit'}
              onClick={() => setDispatchViewMode('audit')}
            >
              Audit
            </button>
          </div>

          {dispatchViewMode === 'audit' ? (
            <section className="audit-tab" aria-label="Audit">
              <div className="audit-hero-stats">
                <AuditStat
                  label="Total slot-fills"
                  value={auditLoads.reduce((sum, l) => sum + l.people, 0)}
                  sub="across all rounds & planes"
                  accent="orange"
                />
                <AuditStat
                  label="Unique jumpers"
                  value={auditJumperRows.length}
                  sub="registered & assigned"
                  accent="grey"
                />
                <AuditStat
                  label="Loads"
                  value={auditLoads.length}
                  sub={`${loadSummaryRows.length} per round x ${auditRoundCount} rounds`}
                  accent="blue"
                />
                <AuditStat
                  label="Rounds"
                  value={auditRoundCount}
                  sub={`${auditRounds.map((r) => r.subtotal).join(' · ')} jumpers`}
                  accent="green"
                />
              </div>
              <div className="audit-two-col">
                <PlaneRoundMatrix
                  rounds={auditRounds}
                  planes={auditPlanes}
                  planeCapacities={auditPlaneCapacities}
                  onCapacityChange={(planeName, nextValue) =>
                    setAuditPlaneCapacities((prev) => ({ ...prev, [planeName]: nextValue }))
                  }
                />
                <AuditBarChart rounds={auditRounds} planes={auditPlanes} />
              </div>
              <AuditLoadsTable
                loads={auditLoads}
                planeCapacities={auditPlaneCapacities}
              />
              <AuditJumperTable
                jumpers={auditJumperRows}
                rounds={auditRounds}
              />
            </section>
          ) : (
            <div className="event-day-manifest-dispatch-stack">
          <section className="event-day-manifest-opsbar edm-stack-opsbar">
            <div className="event-day-manifest-opsbar-round-label">Round {selectedRound}</div>
            <div className="event-day-manifest-opsbar-badges">
              <div className="event-day-manifest-opsbar-badge">
                <span className="event-day-manifest-opsbar-badge-label">On load</span>
                <span className="event-day-manifest-opsbar-badge-value">{roundOpsSummary.onLoadPeople}</span>
              </div>
              <div
                className={`event-day-manifest-opsbar-badge event-day-manifest-opsbar-badge--offload${roundOpsSummary.offLoadPeople > 0 ? ' has-missing' : ''}`}
              >
                <span className="event-day-manifest-opsbar-badge-label">Off load</span>
                <span className="event-day-manifest-opsbar-badge-value">{roundOpsSummary.offLoadPeople}</span>
              </div>
              <div className="event-day-manifest-opsbar-badge">
                <span className="event-day-manifest-opsbar-badge-label">Total</span>
                <span className="event-day-manifest-opsbar-badge-value">{roundOpsSummary.totalTracked}</span>
              </div>
            </div>
            <div className="event-day-manifest-opsbar-progress-wrap" aria-hidden>
              <div className="event-day-manifest-opsbar-progress">
                <div
                  className={`event-day-manifest-opsbar-progress-fill${roundOpsSummary.totalTracked > 0 && roundOpsSummary.onLoadPeople >= roundOpsSummary.totalTracked ? ' is-complete' : ''}`}
                  style={{
                    width: `${
                      roundOpsSummary.totalTracked > 0
                        ? Math.min(100, Math.round((100 * roundOpsSummary.onLoadPeople) / roundOpsSummary.totalTracked))
                        : 0
                    }%`,
                  }}
                />
              </div>
              <span className="event-day-manifest-opsbar-progress-label">
                Placed: {roundOpsSummary.onLoadPeople} / {roundOpsSummary.totalTracked || '—'}
              </span>
            </div>
            <button
              type="button"
              className="event-day-manifest-opsbar-help-btn"
              aria-label="Round scope help"
              title={`Edits affect Round ${selectedRound} only unless you use explicit apply-forward actions.`}
            >
              ?
            </button>
          </section>

          <div className="event-day-manifest-round-tab-bar">
            <div className="event-day-manifest-rounds edm-stack-rounds">
              {Array.from({ length: Math.min(Math.max(manifest?.cycles || 4, 1), 12) }, (_, i) => i + 1).map(
                (r) => (
                <button
                  key={r}
                  type="button"
                  className={`event-day-manifest-round-btn${selectedRound === r ? ' event-day-manifest-round-btn--active' : ''}`}
                  onClick={() => setSelectedRound(r)}
                >
                  Round {r}
                </button>
              ))}
            </div>
          </div>

          <section className="event-day-manifest-dispatch-header edm-stack-planner">
            <button
              type="button"
              className={`event-day-manifest-other-toggle event-day-manifest-setup-toggle${dispatchHeaderExpanded ? ' is-expanded' : ''}`}
              onClick={() => setDispatchHeaderExpanded((e) => !e)}
              aria-expanded={dispatchHeaderExpanded}
            >
              <span className="event-day-manifest-other-chevron">
                ▸
              </span>
              Round {selectedRound} setup
            </button>
            <div className={`event-day-manifest-dispatch-planner-collapse${dispatchHeaderExpanded ? ' is-expanded' : ''}`}>
              <div className="event-day-manifest-dispatch-planner-table-wrap">
              <table className="event-day-manifest-dispatch-planner-table">
                <thead>
                  <tr>
                    <th>Plane</th>
                    {roundPlannerRows[0]?.loads.map((l) => (
                      <th key={l.loadIndex}>Load {l.loadIndex}</th>
                    ))}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {roundPlannerRows.map((row) => (
                    <tr key={row.plane.id}>
                      <td>
                        <span className="event-day-manifest-plane-label event-day-manifest-plane-label--readonly">
                          {planeNamingState.planeNamesById?.[row.plane.id] || row.plane.label || row.plane.id}
                        </span>
                      </td>
                      {row.loads.map((l) => {
                        const plannerKey = `${row.plane.id}:${l.loadIndex}`;
                        const plannerDisplayLabel =
                          editingLoadLabel[plannerKey] !== undefined
                            ? editingLoadLabel[plannerKey]
                            : l.label;
                        const plannerAfter = cellsAfterAnchor(
                          row.plane.id,
                          selectedRound,
                          l.loadIndex,
                          manifest?.slots || [],
                          manifest?.cycles
                        );
                        const plannerCanPopulate =
                          parseLoadLabel(plannerDisplayLabel) && plannerAfter.length > 0;
                        const runPlannerDrop = (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverPlannerKey(null);
                          const ds = dragStateRef.current;
                          setDragState(null);
                          if (!ds) return;
                          handlePlannerLoadCellDrop(row.plane.id, l.loadIndex, ds);
                        };
                        return (
                          <td key={plannerKey} className="event-day-manifest-planner-load-cell">
                            <div
                              className={
                                dragOverPlannerKey === plannerKey
                                  ? 'event-day-manifest-planner-load-drop event-day-manifest-planner-load-drop--drag-over'
                                  : 'event-day-manifest-planner-load-drop'
                              }
                              onDragOver={(e) => {
                                const t = dragStateRef.current?.type;
                                if (t === 'offPlane' || t === 'offPlaneTeam' || t === 'teamFromOther') {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDragOverPlannerKey(plannerKey);
                                }
                              }}
                              onDragLeave={(e) => {
                                if (e.currentTarget.contains(e.relatedTarget)) return;
                                setDragOverPlannerKey((prev) => (prev === plannerKey ? null : prev));
                              }}
                              onDrop={runPlannerDrop}
                            >
                              <div className="event-day-manifest-planner-load-input-row">
                                <input
                                  type="text"
                                  className="event-day-manifest-planner-load-input"
                                  value={
                                    editingLoadLabel[plannerKey] !== undefined
                                      ? editingLoadLabel[plannerKey]
                                      : l.label
                                  }
                                  onChange={(e) =>
                                    setEditingLoadLabel((prev) => ({
                                      ...prev,
                                      [plannerKey]: e.target.value,
                                    }))
                                  }
                                  onBlur={(e) => {
                                    const v = (
                                      editingLoadLabel[plannerKey] !== undefined
                                        ? editingLoadLabel[plannerKey]
                                        : e.target.value
                                    ).trim();
                                    saveLoadLabelForVisibleRound(row.plane.id, l.loadIndex, v);
                                    setEditingLoadLabel((prev) => {
                                      const next = { ...prev };
                                      delete next[plannerKey];
                                      return next;
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.currentTarget.blur();
                                  }}
                                  title={`Load name for round ${selectedRound} only (${l.people} people · ${l.teams} teams on this load). Drop off-load person or team here.`}
                                  onClick={(e) => e.stopPropagation()}
                                  onDragOver={(e) => {
                                    const t = dragStateRef.current?.type;
                                    if (t === 'offPlane' || t === 'offPlaneTeam' || t === 'teamFromOther') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setDragOverPlannerKey(plannerKey);
                                    }
                                  }}
                                  onDrop={runPlannerDrop}
                                />
                                {plannerCanPopulate && (
                                    <button
                                      type="button"
                                      className="event-day-manifest-planner-populate-btn"
                                      onClick={(ev) => {
                                        ev.stopPropagation();
                                        const labelToUse =
                                          editingLoadLabel[plannerKey] !== undefined
                                            ? editingLoadLabel[plannerKey]
                                            : getLoadLabel(row.plane.id, l.loadIndex);
                                        populateLoadLabelsFrom(
                                          row.plane.id,
                                          l.loadIndex,
                                          labelToUse || undefined,
                                          selectedRound
                                        );
                                      }}
                                      title="Fill later loads on this plane (including later rounds) with the same name stem and incrementing numbers"
                                    >
                                      Populate from here
                                    </button>
                                  )}
                              </div>
                            </div>
                          </td>
                        );
                      })}
                      <td>
                        <button
                          type="button"
                          className="event-day-manifest-btn event-day-manifest-btn--outline"
                          onClick={() => handleAddLoadToPlaneRow(selectedRound, row.plane.id)}
                        >
                          + Load
                        </button>
                        <button
                          type="button"
                          className="event-day-manifest-btn event-day-manifest-btn--outline"
                          onClick={() => handleDeletePlaneRow(row.plane.id)}
                          title="Delete plane row (warns if row not empty)"
                        >
                          Delete row
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="event-day-manifest-dispatch-planner-footer">
                <button
                  type="button"
                  className="event-day-manifest-btn event-day-manifest-btn--outline"
                  onClick={handleAddPlaneRow}
                >
                  + Plane row
                </button>
              </div>
            </div>
            </div>
          </section>

          <section className="event-day-manifest-offplane edm-tracker">
            <div className="edm-tracker-header">
              <span className="edm-tracker-title">
                Behind / Off-load tracker - Round {selectedRound}
              </span>
              <div className="edm-tracker-legend" aria-label="Legend">
                <span className="edm-legend-item">
                  <span className="edm-legend-pip" style={{ background: 'var(--pip-covered)' }} />
                  covered
                </span>
                <span className="edm-legend-item">
                  <span className="edm-legend-pip" style={{ background: 'var(--pip-missing-current)' }} />
                  missing now
                </span>
                <span className="edm-legend-item">
                  <span className="edm-legend-pip" style={{ background: 'var(--pip-missed-past)' }} />
                  missed past
                </span>
                <span className="edm-legend-item">
                  <span className="edm-legend-pip" style={{ background: 'var(--pip-future)' }} />
                  future
                </span>
                <span className="edm-legend-item">
                  <span className="edm-legend-pip" style={{ background: 'var(--pip-double)' }} />
                  double load
                </span>
              </div>
            </div>
            <div className="edm-tracker-body">
              {offPlanePeopleCurrentRound.length === 0 ? (
                <div className="edm-tracker-empty">All teams placed ✓</div>
              ) : (
                (() => {
                  const allPeople = offPlaneGroupedByTeam.flatMap((team) =>
                    (team.members || []).map((m) => ({
                      ...m,
                      teamNumber: m.teamNumber || team.teamNumber,
                    }))
                  );
                  const needsLoad = allPeople.filter((m) => !m.onSelectedRound);
                  const onLoad = allPeople.filter((m) => m.onSelectedRound && !m.heavilyMissing);
                  const missingRounds = allPeople.filter((m) => m.onSelectedRound && m.heavilyMissing);
                  return (
                    <>
                      {needsLoad.length > 0 && (
                        <div className="edm-lane">
                          <div className="edm-lane-header edm-lane-header--needs">
                            <span>⚠ Needs load</span>
                            <span className="edm-lane-count">{needsLoad.length}</span>
                          </div>
                          <div className="edm-lane-chips">
                            {needsLoad.map((person) => (
                              <PersonChip
                                key={person.emailKey}
                                member={person}
                                status="needs"
                                currentRound={selectedRound}
                                setDragState={setDragState}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {onLoad.length > 0 && (
                        <div className="edm-lane">
                          <div className="edm-lane-header edm-lane-header--on">
                            <span>✓ On a load</span>
                            <span className="edm-lane-count">{onLoad.length}</span>
                          </div>
                          <div className="edm-lane-chips">
                            {onLoad.map((person) => (
                              <PersonChip
                                key={person.emailKey}
                                member={person}
                                status="on"
                                currentRound={selectedRound}
                                setDragState={setDragState}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                      {missingRounds.length > 0 && (
                        <div className="edm-lane">
                          <div className="edm-lane-header edm-lane-header--warn">
                            <span>⚠ R{selectedRound} only — missing other rounds</span>
                            <span className="edm-lane-count">{missingRounds.length}</span>
                          </div>
                          <div className="edm-lane-chips">
                            {missingRounds.map((person) => (
                              <PersonChip
                                key={person.emailKey}
                                member={person}
                                status="warn"
                                currentRound={selectedRound}
                                setDragState={setDragState}
                              />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()
              )}
            </div>
          </section>

          <section className="event-day-manifest-other edm-stack-other">
            <button
              type="button"
              className="event-day-manifest-other-toggle"
              onClick={() => setOtherSectionExpanded((e) => !e)}
              aria-expanded={otherSectionExpanded}
            >
              <span className="event-day-manifest-other-chevron">
                {otherSectionExpanded ? '▾' : '▸'}
              </span>
              Extra jumps (off grid)
              {otherJumps.length > 0 && (
                <span className="event-day-manifest-other-count">({otherJumps.length})</span>
              )}
            </button>
            {otherSectionExpanded && (
              <>
                <p className="event-day-manifest-other-desc">
                  Jumps not on the meet load board (DZ fun jumps, etc.). Drag people or teams here, or drag from the
                  off-load tracker, or add manually.
                </p>
                <div className="event-day-manifest-other-list">
                  {otherJumps.map((jump, idx) => (
                    <div
                      key={idx}
                      className={`event-day-manifest-other-jump${dragOverOtherIdx === idx ? ' event-day-manifest-other-jump--drag-over' : ''}`}
                      onDragOver={(e) => {
                        if (dragState) {
                          e.preventDefault();
                          e.stopPropagation();
                          setDragOverOtherIdx(idx);
                        }
                      }}
                      onDragLeave={() => setDragOverOtherIdx(null)}
                      onDrop={async (e) => {
                        e.preventDefault();
                        setDragOverOtherIdx(null);
                        const ds = dragState;
                        setDragState(null);
                        if (!ds) return;
                        try {
                          if (ds.type === 'offPlane' && ds.person?.email) {
                            const normalized = (ds.person.email || '').toLowerCase().trim();
                            const flyer = flyersByEmail.get(normalized);
                            const displayEmail = flyer
                              ? (flyer.email || ds.person.email)
                              : ds.person.email;
                            const src = findManifestSlotWithParticipantInCycle(
                              ds.person.email,
                              selectedRound
                            );
                            if (src) {
                              handleDropOnOtherJump(idx, src, 'participant', ds.person.email);
                            } else {
                              await addEmailsToOtherJump(idx, [displayEmail], null);
                            }
                            return;
                          }
                          if (ds.type === 'offPlaneTeam' && ds.teamId) {
                            const team = teams.find((t) => String(t.id) === String(ds.teamId));
                            if (team) {
                              let teamForEmails = team;
                              try {
                                const teamSnap = await getDoc(doc(firestore, 'teams', team.id));
                                const teamData = teamSnap.data();
                                if (teamData) teamForEmails = { id: teamSnap.id, ...teamData };
                              } catch {
                                /* use in-memory team */
                              }
                              const emails = participantEmailsFromTeam(teamForEmails);
                              await addEmailsToOtherJump(idx, emails, {
                                teamId: team.id,
                                teamNumber: team.teamNumber,
                              });
                            }
                            return;
                          }
                          if (ds.type === 'participant' && ds.email) {
                            handleDropOnOtherJump(idx, ds.sourceSlot, 'participant', ds.email);
                          } else if (ds.type === 'team') {
                            handleDropOnOtherJump(idx, ds.sourceSlot, 'team');
                          }
                        } catch (err) {
                          setError(err?.message || 'Failed to add to extra jump');
                        }
                      }}
                    >
                        <div className="event-day-manifest-other-jump-header">
                        <input
                          type="text"
                          className="event-day-manifest-other-jump-label"
                          value={editingOtherJumpLabel[idx] !== undefined ? editingOtherJumpLabel[idx] : (jump.label ?? '')}
                          onChange={(e) => setEditingOtherJumpLabel((prev) => ({ ...prev, [idx]: e.target.value }))}
                          onBlur={(e) => {
                            const v = (editingOtherJumpLabel[idx] !== undefined ? editingOtherJumpLabel[idx] : e.target.value).trim();
                            updateOtherJump(idx, { label: v || undefined });
                            setEditingOtherJumpLabel((prev) => {
                              const next = { ...prev };
                              delete next[idx];
                              return next;
                            });
                          }}
                          placeholder={`Jump ${idx + 1} or e.g. DZ Otter`}
                        />
                        <button
                          type="button"
                          className="event-day-manifest-remove-jump-btn"
                          onClick={() => removeOtherJump(idx)}
                          title="Remove this jump"
                        >
                          Remove
                        </button>
                      </div>
                      {(jump.teamId != null || jump.teamNumber != null) && (
                        <div
                          className="event-day-manifest-other-jump-team"
                          draggable
                          onDragStart={(e) => {
                            setDragState({
                              type: 'teamFromOther',
                              otherJumpIndex: idx,
                              teamId: jump.teamId,
                              teamNumber: jump.teamNumber,
                              participantEmails: jump.participantEmails || [],
                            });
                            e.dataTransfer.setData('text/plain', `teamFromOther:${jump.teamNumber ?? ''}`);
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => setDragState(null)}
                        >
                          <span className="event-day-manifest-other-jump-drag-handle" title="Drag back to a load">⋮⋮</span>
                          #{jump.teamNumber ?? '?'}
                          <span className="event-day-manifest-other-jump-drag-hint">Drag to load</span>
                        </div>
                      )}
                    {(jump.participantEmails || []).map((email, peIdx) => {
                      const f = flyersByEmail.get((email || '').toLowerCase());
                      const name = f ? getDisplayName(f) || email : email;
                      return (
                        <div
                          key={`${(email || '').toLowerCase()}-${peIdx}`}
                          className="event-day-manifest-participant"
                        >
                          <span>{name || email || '—'}</span>
                          <button
                            type="button"
                            className="event-day-manifest-remove-btn"
                            onClick={() => {
                              const next = (jump.participantEmails || []).filter(
                                (e) => (e || '').toLowerCase() !== (email || '').toLowerCase()
                              );
                              updateOtherJump(idx, { participantEmails: next });
                            }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                    <ParticipantAdd
                      flyers={flyers}
                      onAdd={(email) => {
                        const normalized = (email || '').trim().toLowerCase();
                        if (!normalized) return;
                        const current = (jump.participantEmails || []).map((e) => (e || '').toLowerCase());
                        if (current.includes(normalized)) return;
                        const flyer = flyersByEmail.get(normalized);
                        const displayEmail = flyer ? (flyer.email || normalized) : normalized;
                        updateOtherJump(idx, { participantEmails: [...(jump.participantEmails || []), displayEmail] });
                      }}
                    />
                  </div>
                ))}
              <button
                type="button"
                className="event-day-manifest-add-jump-btn"
                onClick={addOtherJump}
              >
                + Add jump
              </button>
            </div>
              </>
            )}
          </section>
          <div
            className="event-day-manifest-dispatch-board-separator"
            role="banner"
            aria-label={`Dispatch board, Round ${selectedRound}`}
          >
            <span className="edm-banner-label">Dispatch board</span>
            <div className="edm-banner-divider" aria-hidden />
            <div className="edm-round-pill">Round {selectedRound}</div>
            <div className="event-day-manifest-opsbar-progress-wrap edm-banner-progress">
              <div className="event-day-manifest-opsbar-progress">
                <div
                  className={`event-day-manifest-opsbar-progress-fill${
                    roundOpsSummary.totalTracked > 0 &&
                    roundOpsSummary.onLoadPeople >= roundOpsSummary.totalTracked
                      ? ' is-complete'
                      : ''
                  }`}
                  style={{
                    width: `${
                      roundOpsSummary.totalTracked > 0
                        ? Math.min(
                            100,
                            Math.round((100 * roundOpsSummary.onLoadPeople) / roundOpsSummary.totalTracked)
                          )
                        : 0
                    }%`,
                  }}
                />
              </div>
              <span className="edm-banner-progress-text">
                {roundOpsSummary.onLoadPeople} / {roundOpsSummary.totalTracked || '—'} placed
              </span>
            </div>
          </div>

          <div className="event-day-manifest-grid edm-stack-grid">
            <div className="event-day-manifest-grid-header">
              <span>Load</span>
              {planes.map((p) => (
                <span key={p.id} className={`event-day-manifest-plane-header event-day-manifest-plane-${p.id}`}>
                  <span className="event-day-manifest-plane-label event-day-manifest-plane-label--readonly">
                    {p.label || p.id}
                  </span>
                  <span className="edm-grid-round-tag" aria-label={`Round ${selectedRound}`}>
                    R{selectedRound}
                  </span>
                  <button
                    type="button"
                    className="event-day-manifest-clear-plane-btn"
                    onClick={() => handleClearPlane(p.id)}
                    title={`Clear all participants on ${p.label} (all rounds)`}
                  >
                    Clear plane
                  </button>
                </span>
              ))}
            </div>
            <div className="event-day-manifest-grid-body">
              {loadIndices.map((loadIndex) => (
                <div key={loadIndex} className="event-day-manifest-grid-row edm-load-row">
                  <div className="event-day-manifest-load-cell edm-load-cell edm-load-cell--number-only">
                    <span className="edm-load-number">Load {loadIndex}</span>
                  </div>
                  {planes.map((p) => {
                    const cellKey = `${String(loadIndex)}:${String(p.id)}`;
                    const cellUi = loadGridUI[cellKey] ?? { collapsed: false, done: false };
                    const done = !!cellUi.done;
                    const collapsed = !!cellUi.collapsed;
                    const defaultLabel = getDefaultLoadLabel(p.id, loadIndex);
                    const peopleSlots = getPeopleCount(p.id, loadIndex);
                    const teamRows = getTeamsCount(p.id, loadIndex);
                    return (
                    <div
                      key={p.id}
                      className={`event-day-manifest-plane-col event-day-manifest-plane-${p.id}-col${
                        done ? ' edm-plane-col--done' : ''
                      }`}
                    >
                      <div
                        className={`event-day-manifest-cell-load-header edm-cell-load-header edm-plane-load-header${
                          done ? ' edm-cell-load-header--done' : ''
                        }`}
                        data-clickable={collapsed ? 'true' : undefined}
                        onClick={() => {
                          if (collapsed) patchLoadGridUI(loadIndex, p.id, { collapsed: false });
                        }}
                        title={collapsed ? "Click to expand this plane's slots" : undefined}
                      >
                        <div
                          className="edm-plane-load-toolbar"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            className="edm-load-toggle-btn edm-load-toggle-btn--compact"
                            onClick={() => patchLoadGridUI(loadIndex, p.id, { collapsed: !collapsed })}
                            title={collapsed ? 'Expand slots' : 'Collapse slots'}
                            aria-expanded={!collapsed}
                          >
                            <span
                              className={`edm-load-chevron${
                                collapsed ? ' edm-load-chevron--closed' : ' edm-load-chevron--open'
                              }`}
                              aria-hidden
                            >
                              ▼
                            </span>
                          </button>
                          <button
                            type="button"
                            className={`edm-done-btn edm-done-btn--compact${done ? ' edm-done-btn--done' : ''}`}
                            onClick={() => {
                              if (!done) patchLoadGridUI(loadIndex, p.id, { done: true, collapsed: true });
                              else patchLoadGridUI(loadIndex, p.id, { done: false });
                            }}
                            title={
                              done
                                ? 'Clear done for this plane / load'
                                : "Mark this plane's load as done (collapses slots)"
                            }
                          >
                            ✓
                          </button>
                        </div>
                        <span
                          className="event-day-manifest-load-label-input event-day-manifest-load-label-input--readonly edm-load-name"
                          title={`Load name for round ${selectedRound}`}
                        >
                          {getLoadLabel(p.id, loadIndex) || defaultLabel}
                        </span>
                        {done ? (
                          <span className="event-day-manifest-slot-count edm-slot-count edm-slot-count--done">
                            ✓ Done
                          </span>
                        ) : (
                          <span
                            className="event-day-manifest-slot-count edm-slot-count"
                            title={`${peopleSlots} people; ${teamRows} teams with assignments`}
                          >
                            <span title="participants">👤 {peopleSlots}</span>
                            <span className="event-day-manifest-slot-count-sep edm-slot-count-sep" aria-hidden>
                              ·
                            </span>
                            <span title="teams">⬡ {teamRows}</span>
                          </span>
                        )}
                      </div>
                      {!collapsed &&
                      getDisplaySlotRows(p.id, loadIndex).map(({ slotIdx, slot }) => {
                        const key = `${p.id}:${loadIndex}:${slotIdx}`;
                        const slotKeyWithRound = `${p.id}:${loadIndex}:${slotIdx}:${selectedRound}`;
                        const slotDomId = `edm-slot-${selectedRound}-${toDomSafeIdPart(p.id)}-${loadIndex}-${slotIdx}`;
                        const isSlotHighlighted = highlightedSlotKey === slotKeyWithRound;
                        return (
                          <div
                            key={key}
                            id={slotDomId}
                            className={`event-day-manifest-slot${dragOverKey === key && (slot || dragState?.type === 'team') ? ' event-day-manifest-slot--drag-over' : ''}${isSlotHighlighted ? ' event-day-manifest-slot--search-highlight' : ''}`}
                            onDragOver={(e) => {
                              if (dragState) {
                                const canDrop =
                                  (slot && (dragState.type === 'participant' || dragState.type === 'team')) ||
                                  (!slot && dragState.type === 'team') ||
                                  dragState.type === 'teamFromOther' ||
                                  (slot && dragState.type === 'offPlane') ||
                                  (!slot && dragState.type === 'offPlane') ||
                                  (!slot && dragState.type === 'offPlaneTeam');
                                if (canDrop) {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setDragOverKey(key);
                                }
                              }
                            }}
                            onDragLeave={() => setDragOverKey(null)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setDragOverKey(null);
                              if (!dragState) return;
                              if (dragState.type === 'participant' && dragState.email && slot) {
                                handleParticipantDrop(slot, dragState.sourceSlot, dragState.email);
                              } else if (dragState.type === 'team') {
                                const dropTarget = slot || { planeId: p.id, loadIndex, slotIndexInLoad: slotIdx };
                                handleTeamDrop(dropTarget, dragState.sourceSlot);
                              } else if (dragState.type === 'teamFromOther') {
                                const dropTarget = slot || { planeId: p.id, loadIndex, slotIndexInLoad: slotIdx };
                                handleDropTeamFromOther(dragState, dropTarget);
                              } else if (dragState.type === 'offPlane' && slot) {
                                handleAssignOffPlanePersonToSlot(slot, dragState.person);
                              } else if (dragState.type === 'offPlane' && !slot) {
                                handleAssignOffPlanePersonToEmptySlot(
                                  p.id,
                                  loadIndex,
                                  slotIdx,
                                  dragState.person
                                );
                              } else if (dragState.type === 'offPlaneTeam' && !slot) {
                                const t = teams.find((tm) => String(tm.id) === String(dragState.teamId));
                                if (t) handleAssignTeamToEmptySlot(p.id, loadIndex, slotIdx, t);
                              }
                              setDragState(null);
                            }}
                          >
                            {slot && slot.teamId ? (
                              <ManifestSlotCard
                                slot={slot}
                                slotKeyWithRound={slotKeyWithRound}
                                flyers={flyers}
                                flyersByEmail={flyersByEmail}
                                highlightedSlotKey={highlightedSlotKey}
                                highlightedEmailKey={highlightedEmailKey}
                                resolveSlotTeamNumber={resolveSlotTeamNumber}
                                getSlotParticipantEmailsSorted={getSlotParticipantEmailsSorted}
                                getParticipantRoleForSlot={getParticipantRoleForSlot}
                                handleAddParticipantByScope={handleAddParticipantByScope}
                                handleRemoveParticipantByScope={handleRemoveParticipantByScope}
                                handleOffloadAllParticipantsByScope={handleOffloadAllParticipantsByScope}
                                setDragState={setDragState}
                              />
                            ) : (
                              <div className="event-day-manifest-slot-empty">
                                {(dragState?.type === 'team' ||
                                  dragState?.type === 'teamFromOther' ||
                                  dragState?.type === 'offPlane' ||
                                  dragState?.type === 'offPlaneTeam') && (
                                  <span className="event-day-manifest-slot-empty-hint">
                                    Drop team or off-load person here
                                  </span>
                                )}
                                <TeamSlotAdd
                                  teams={teamsSorted}
                                  flyersByEmail={flyersByEmail}
                                  selectedRound={selectedRound}
                                  onSelect={(team) =>
                                    handleAssignTeamToEmptySlot(p.id, loadIndex, slotIdx, team)
                                  }
                                />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
        </div>
      )}
      {destructiveUndo && (
        <div className="event-day-manifest-undo-toast" role="status" aria-live="polite">
          <div className="event-day-manifest-undo-toast-body">
            <span>{destructiveUndo.message}</span>
            <div className="event-day-manifest-undo-toast-actions">
              <button
                type="button"
                className="event-day-manifest-undo-toast-undo"
                onClick={() => destructiveUndo.revert()}
              >
                Undo
              </button>
            </div>
          </div>
          <div className="event-day-manifest-undo-progress-track">
            <div
              key={destructiveUndo.message}
              className="event-day-manifest-undo-progress-bar"
            />
          </div>
        </div>
      )}
      {edmExport && (
        <AdminColumnExportModal
          mode={edmExport.mode}
          columns={getEdmExportColumns(edmExport.kind)}
          selectedIds={edmExportCols}
          onChangeSelected={setEdmExportCols}
          onCancel={() => setEdmExport(null)}
          onConfirm={runEdmExport}
          onResetDefaults={resetEdmExportCols}
        />
      )}
    </div>
  );
}

function SortTh({ label, sortKey, current, dir, onSort }) {
  const active = current === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey, active && dir === 'asc' ? 'desc' : 'asc')}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      {label}
      {active ? (dir === 'asc' ? ' ▲' : ' ▼') : ''}
    </th>
  );
}

function AuditStat({ label, value, sub, accent }) {
  return (
    <div className={`audit-stat-card audit-stat-card--${accent || 'grey'}`}>
      <div className="audit-stat-label">{label}</div>
      <div className="audit-stat-value">{value}</div>
      <div className="audit-stat-sub">{sub}</div>
    </div>
  );
}

function PlaneChip({ planeName, planeId, label, small }) {
  const tone = getAuditPlaneTheme(planeName, planeId);
  return (
    <span className={`audit-plane-chip ${tone.chipClass}${small ? ' audit-plane-chip--small' : ''}`}>
      {label || planeName}
    </span>
  );
}

function GapBadge({ gap }) {
  if (gap == null) return <span className="audit-gap-badge audit-gap-badge--none">—</span>;
  if (gap < 0) return <span className="audit-gap-badge audit-gap-badge--under">{gap}</span>;
  if (gap > 0) return <span className="audit-gap-badge audit-gap-badge--over">+{gap}</span>;
  return <span className="audit-gap-badge audit-gap-badge--even">✓</span>;
}

function PlaneRoundMatrix({ rounds, planes, planeCapacities, onCapacityChange }) {
  const maxCount = Math.max(1, ...planes.flatMap((p) => p.roundTotals || [0]));
  const grandTotal = rounds.reduce((sum, r) => sum + (r.subtotal || 0), 0);
  return (
    <div className="audit-matrix-section">
      <div className="audit-section-header">
        <span className="audit-section-title">Plane × Round breakdown</span>
        <div className="audit-matrix-cap-inputs">
          <span className="audit-cap-label">Slot capacity per plane:</span>
          {planes.map((p) => (
            <label key={p.name} className="audit-cap-field">
              <span style={{ color: p.color }}>{p.name}</span>
              <input
                type="number"
                min="0"
                placeholder="—"
                value={planeCapacities[p.name] ?? ''}
                onChange={(e) => onCapacityChange(p.name, e.target.value ? Number(e.target.value) : null)}
              />
            </label>
          ))}
        </div>
      </div>
      <table className="audit-matrix-table">
        <thead>
          <tr>
            <th>Plane</th>
            {rounds.map((r) => (
              <th key={r.number}>R{r.number}</th>
            ))}
            <th className="audit-matrix-total-col">Total</th>
            <th title="Actual fills vs. your capacity × rounds">Gap</th>
          </tr>
        </thead>
        <tbody>
          {planes.map((p) => {
            const cap = planeCapacities[p.name];
            const totalCap = cap != null ? cap * rounds.length : null;
            const gap = totalCap != null ? p.meetTotal - totalCap : null;
            return (
              <tr key={p.name}>
                <td>
                  <span className="audit-plane-badge">
                    <span className="audit-plane-dot" style={{ background: p.color }} />
                    {p.name}
                  </span>
                </td>
                {p.roundTotals.map((count, i) => (
                  <td key={i} className="audit-matrix-cell">
                    <span className="audit-matrix-number">{count}</span>
                    <div
                      className="audit-matrix-bar"
                      style={{ width: `${Math.round((count / maxCount) * 60)}px`, background: p.color, opacity: 0.7 }}
                    />
                  </td>
                ))}
                <td className="audit-matrix-total-col">
                  <span className="audit-matrix-number">{p.meetTotal}</span>
                  <div className="audit-matrix-bar" style={{ width: '60px', background: p.color, opacity: 0.7 }} />
                </td>
                <td><GapBadge gap={gap} /></td>
              </tr>
            );
          })}
          <tr className="audit-matrix-total-row">
            <td>TOTAL</td>
            {rounds.map((r) => (
              <td key={r.number}>{r.subtotal}</td>
            ))}
            <td className="audit-matrix-total-col" style={{ color: 'var(--fm-orange)' }}>{grandTotal}</td>
            <td>—</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function AuditBarChart({ rounds, planes }) {
  const maxTotal = Math.max(1, ...rounds.map((r) => r.subtotal || 0));
  const grandTotal = rounds.reduce((sum, r) => sum + (r.subtotal || 0), 0);
  return (
    <div className="audit-chart">
      <div className="audit-section-header">
        <span className="audit-section-title">Round distribution</span>
        <button className="audit-print-btn no-print" type="button" onClick={() => window.print()}>
          🖨 Print audit
        </button>
      </div>
      <div className="audit-chart-bars">
        {rounds.map((r) => (
          <div key={r.number} className="audit-chart-round">
            <div className="audit-chart-stack">
              {planes.map((p) => {
                const count = p.roundTotals[r.number - 1] || 0;
                const heightPct = maxTotal > 0 ? (count / maxTotal) * 100 : 0;
                return count > 0 ? (
                  <div
                    key={p.name}
                    className="audit-chart-segment"
                    style={{ height: `${heightPct}%`, background: p.color }}
                    title={`${p.name}: ${count}`}
                  >
                    {count >= 3 && <span className="audit-chart-seg-label">{count}</span>}
                  </div>
                ) : null;
              })}
            </div>
            <div className="audit-chart-xlabel">R{r.number}</div>
            <div className="audit-chart-total">{r.subtotal}</div>
          </div>
        ))}
      </div>
      <div className="audit-chart-proportions">
        <div className="audit-prop-label">Meet total by plane</div>
        {planes.map((p) => (
          <div key={p.name} className="audit-prop-row">
            <span className="audit-prop-name" style={{ color: p.text }}>{p.name}</span>
            <div className="audit-prop-bar-wrap">
              <div
                className="audit-prop-bar"
                style={{ width: `${grandTotal > 0 ? (p.meetTotal / grandTotal) * 100 : 0}%`, background: p.color }}
              />
            </div>
            <span className="audit-prop-count">{p.meetTotal}</span>
            <span className="audit-prop-pct">{grandTotal > 0 ? Math.round((p.meetTotal / grandTotal) * 100) : 0}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AuditLoadsTable({ loads, planeCapacities }) {
  const [sortKey, setSortKey] = useState('round');
  const [sortDir, setSortDir] = useState('asc');
  const [filterPlane, setFilterPlane] = useState(null);
  const [filterRound, setFilterRound] = useState(null);
  const [search, setSearch] = useState('');
  const maxPeople = Math.max(1, ...loads.map((l) => l.people || 0));
  const planes = useMemo(
    () => [...new Set(loads.map((l) => l.planeName).filter(Boolean))],
    [loads]
  );

  const filtered = loads
    .filter((l) => !filterPlane || l.planeName === filterPlane)
    .filter((l) => !filterRound || l.round === filterRound)
    .filter((l) => !search || l.loadName.toLowerCase().includes(search.toLowerCase()));

  const sorted = [...filtered].sort((a, b) => {
    const dir = sortDir === 'asc' ? 1 : -1;
    if (sortKey === 'people') return dir * (a.people - b.people);
    if (sortKey === 'round') return dir * (a.round - b.round) || a.loadOrder - b.loadOrder;
    if (sortKey === 'plane') return dir * a.planeName.localeCompare(b.planeName);
    if (sortKey === 'load') return dir * a.loadName.localeCompare(b.loadName);
    return 0;
  });

  return (
    <div className="audit-loads-section">
      <div className="audit-section-header">
        <span className="audit-section-title">All loads</span>
        <div className="audit-controls">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search load name…" className="audit-search" />
          <div className="audit-filter-pills">
            <button className={`audit-pill audit-pill--all${!filterPlane ? ' active' : ''}`} onClick={() => setFilterPlane(null)}>All planes</button>
            {planes.map((p) => {
              const tone = getAuditPlaneTheme(p, null);
              return (
                <button
                  key={p}
                  className={`audit-pill audit-pill--plane${filterPlane === p ? ' active' : ''}`}
                  data-plane={tone.pillKey}
                  onClick={() => setFilterPlane(filterPlane === p ? null : p)}
                >
                  {p}
                </button>
              );
            })}
          </div>
          <select value={filterRound ?? ''} onChange={(e) => setFilterRound(e.target.value ? Number(e.target.value) : null)} className="audit-select">
            <option value="">All rounds</option>
            {[...new Set(loads.map((l) => l.round))].sort((a, b) => a - b).map((r) => (
              <option key={r} value={r}>Round {r}</option>
            ))}
          </select>
        </div>
      </div>
      <table className="audit-data-table">
        <thead>
          <tr>
            <SortTh label="Round" sortKey="round" current={sortKey} dir={sortDir} onSort={(k, d) => { setSortKey(k); setSortDir(d); }} />
            <SortTh label="Load name" sortKey="load" current={sortKey} dir={sortDir} onSort={(k, d) => { setSortKey(k); setSortDir(d); }} />
            <SortTh label="Plane" sortKey="plane" current={sortKey} dir={sortDir} onSort={(k, d) => { setSortKey(k); setSortDir(d); }} />
            <SortTh label="People" sortKey="people" current={sortKey} dir={sortDir} onSort={(k, d) => { setSortKey(k); setSortDir(d); }} />
            <th title="Your capacity input">Cap</th>
            <th title="Fills vs. capacity">Gap</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((load) => {
            const cap = planeCapacities[load.planeName];
            const gap = cap != null ? load.people - cap : null;
            const tone = getAuditPlaneTheme(load.planeName, load.planeId);
            return (
              <tr key={load.id}>
                <td><span className="audit-round-badge">{load.round}</span></td>
                <td className="audit-load-name">{load.loadName}</td>
                <td><PlaneChip planeName={load.planeName} planeId={load.planeId} /></td>
                <td>
                  <div className="audit-count-cell">
                    <div className="audit-count-bar-wrap">
                      <div className="audit-count-bar" style={{ width: `${(load.people / maxPeople) * 100}%`, background: tone.color }} />
                    </div>
                    <span className="audit-count-num">{load.people}</span>
                  </div>
                </td>
                <td className="audit-cap-cell">{cap ?? '—'}</td>
                <td><GapBadge gap={gap} /></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function jumpTier(count, totalRounds) {
  if (count >= totalRounds) return 'full';
  if (count > 1) return 'partial';
  return 'low';
}

function AuditJumperTable({ jumpers, rounds }) {
  const [sort, setSort] = useState({ key: 'name', dir: 'asc' });
  const [filterJumps, setFilterJumps] = useState(null);
  const [filterPlane, setFilterPlane] = useState(null);
  const [search, setSearch] = useState('');
  const planes = useMemo(() => {
    const set = new Set();
    for (const j of jumpers) {
      for (const a of j.roundAssignments || []) {
        if (a?.planeName) set.add(a.planeName);
      }
    }
    return [...set];
  }, [jumpers]);

  const filtered = jumpers
    .filter((j) => !search || j.name.toLowerCase().includes(search.toLowerCase()) || j.email.toLowerCase().includes(search.toLowerCase()))
    .filter((j) => filterJumps == null || j.meetJumps === filterJumps)
    .filter((j) => !filterPlane || j.roundAssignments.some((a) => a?.planeName === filterPlane));

  const sorted = [...filtered].sort((a, b) => {
    const d = sort.dir === 'asc' ? 1 : -1;
    if (sort.key === 'jumps') return d * (a.meetJumps - b.meetJumps);
    return d * a.name.localeCompare(b.name);
  });

  return (
    <div className="audit-jumpers-section">
      <div className="audit-section-header">
        <span className="audit-section-title">Jumper audit</span>
        <div className="audit-controls">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name/email…" className="audit-search" />
          <select value={filterJumps ?? ''} onChange={(e) => setFilterJumps(e.target.value ? Number(e.target.value) : null)} className="audit-select">
            <option value="">All jump counts</option>
            {Array.from(new Set(jumpers.map((j) => j.meetJumps))).sort((a, b) => a - b).map((n) => (
              <option key={n} value={n}>{n} jump{n === 1 ? '' : 's'}</option>
            ))}
          </select>
          <select value={filterPlane ?? ''} onChange={(e) => setFilterPlane(e.target.value || null)} className="audit-select">
            <option value="">All planes</option>
            {planes.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </div>
      <table className="audit-data-table">
        <thead>
          <tr>
            <SortTh label="Name" sortKey="name" current={sort.key} dir={sort.dir} onSort={(k, d) => setSort({ key: k, dir: d })} />
            <th>Email</th>
            {rounds.map((r) => <th key={r.number}>R{r.number}</th>)}
            <th>Extra</th>
            <SortTh label="Jumps" sortKey="jumps" current={sort.key} dir={sort.dir} onSort={(k, d) => setSort({ key: k, dir: d })} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((j) => (
            <tr key={j.id}>
              <td className="audit-jumper-name">{j.name}</td>
              <td className="audit-jumper-email">{j.email}</td>
              {(j.roundAssignments || []).map((assignment, i) => (
                <td key={i}>
                  {assignment == null ? (
                    <span className="audit-round-empty">—</span>
                  ) : assignment.isDouble ? (
                    <span className="audit-round-double" title={`Double load: ${(assignment.loads || []).join(', ')}`}>
                      ⊕ ×{(assignment.loads || []).length}
                    </span>
                  ) : (
                    <PlaneChip planeName={assignment.planeName} planeId={assignment.planeId} label={assignment.loadName} small />
                  )}
                </td>
              ))}
              <td>{j.extraJumps > 0 ? j.extraJumps : <span className="audit-round-empty">—</span>}</td>
              <td>
                <span className={`audit-jump-badge audit-jump-badge--${jumpTier(j.meetJumps, rounds.length)}`}>
                  {j.meetJumps}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PersonChip({ member, status, currentRound, setDragState }) {
  return (
    <div
      className={`edm-chip edm-chip--${status}`}
      draggable
      onDragStart={(e) => {
        setDragState({ type: 'offPlane', person: member });
        e.dataTransfer.setData('text/plain', member.email);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => setDragState(null)}
      title={`${member.name || member.email} — drag to assign to a load`}
    >
      <span className="edm-chip-drag" aria-hidden>⠿</span>
      <span className="edm-chip-name">{member.name}</span>
      {member.teamNumber ? <span className="edm-chip-team">T#{member.teamNumber}</span> : null}
      <ChipPips
        totalRounds={member.totalRounds}
        currentRound={currentRound}
        roundCoverage={member.roundCoverage}
      />
    </div>
  );
}

function ChipPips({ totalRounds, currentRound, roundCoverage }) {
  return (
    <div className="edm-chip-pips" aria-label="Round coverage">
      {Array.from({ length: totalRounds }, (_, i) => {
        const roundNum = i + 1;
        const isCurrent = roundNum === currentRound;
        const count = roundCoverage?.[i] ?? 0;
        const isPast = roundNum < currentRound;

        let pipClass = 'edm-pip';
        if (count >= 2) pipClass += ' edm-pip--double';
        else if (count === 1) pipClass += ' edm-pip--covered';
        else if (isCurrent) pipClass += ' edm-pip--missing-now';
        else if (isPast) pipClass += ' edm-pip--missed-past';
        else pipClass += ' edm-pip--future';
        if (isCurrent) pipClass += ' edm-pip--current';

        const label =
          count >= 2
            ? `R${roundNum}: on ${count} loads`
            : count === 1
              ? `R${roundNum}: assigned`
              : `R${roundNum}: no assignment`;

        return (
          <div
            key={roundNum}
            className={pipClass}
            title={`${label}${isCurrent ? ' (current round)' : ''}`}
          />
        );
      })}
    </div>
  );
}

function ManifestSlotCard({
  slot,
  slotKeyWithRound,
  flyers,
  flyersByEmail,
  highlightedSlotKey,
  highlightedEmailKey,
  resolveSlotTeamNumber,
  getSlotParticipantEmailsSorted,
  getParticipantRoleForSlot,
  handleAddParticipantByScope,
  handleRemoveParticipantByScope,
  handleOffloadAllParticipantsByScope,
  setDragState,
}) {
  const [rosterOpen, setRosterOpen] = useState(false);
  const [rosterSearch, setRosterSearch] = useState('');
  const [rosterScope, setRosterScope] = useState('this-load');
  const isSlotHighlighted = highlightedSlotKey === slotKeyWithRound;
  const currentEmails = useMemo(
    () => new Set((slot.participantEmails || []).map((email) => (email || '').toLowerCase().trim())),
    [slot.participantEmails]
  );

  const filteredPeople = useMemo(() => {
    const term = (rosterSearch || '').toLowerCase().trim();
    const inSlot = [];
    const outOfSlot = [];
    const seenEmails = new Set();
    for (const f of flyers || []) {
      const email = (f?.email || '').toLowerCase().trim();
      if (!email || seenEmails.has(email)) continue;
      seenEmails.add(email);
      const name = getDisplayName(f) || f.email || '—';
      if (term) {
        const emailText = (f.email || '').toLowerCase();
        const nameText = name.toLowerCase();
        if (!emailText.includes(term) && !nameText.includes(term)) continue;
      }
      const role = getParticipantRoleForSlot(slot, f.email);
      const person = {
        id: f.id || email,
        email: f.email,
        name,
        inSlot: currentEmails.has(email),
        isMentor: role === 'mentor',
        isFreshling: role === 'freshling',
      };
      if (person.inSlot) inSlot.push(person);
      else outOfSlot.push(person);
    }
    return [...inSlot, ...outOfSlot].slice(0, 40);
  }, [flyers, rosterSearch, currentEmails, getParticipantRoleForSlot, slot]);

  const scopeToExisting = (scope) => {
    if (scope === 'round-onward') return 'forward';
    if (scope === 'all-rounds') return 'all';
    return 'load';
  };

  const togglePerson = (email, scope) => {
    const key = (email || '').toLowerCase().trim();
    if (!key) return;
    const existingScope = scopeToExisting(scope);
    if (currentEmails.has(key)) handleRemoveParticipantByScope(slot, email, existingScope);
    else handleAddParticipantByScope(slot, email, existingScope);
  };

  return (
    <div className="edm-card">
      <div className="edm-card-header">
        <span
          className="edm-drag"
          aria-hidden
          title="Drag to swap with another slot"
          draggable
          onDragStart={(e) => {
            setDragState({ type: 'team', sourceSlot: slot });
            e.dataTransfer.setData('text/plain', 'team');
            e.dataTransfer.effectAllowed = 'move';
          }}
          onDragEnd={() => setDragState(null)}
        >
          ⠿
        </span>
        <span className="edm-team-num">#{resolveSlotTeamNumber(slot)}</span>
        <div className="edm-header-actions">
          <button
            type="button"
            className="edm-btn edm-btn--skip-load"
            onClick={() => handleOffloadAllParticipantsByScope(slot, 'load')}
            title="Mark whole team absent for this load; they remain on future loads"
          >
            - Skip team
          </button>
          <button
            type="button"
            className="edm-btn edm-btn--drop-fwd"
            onClick={() => handleOffloadAllParticipantsByScope(slot, 'forward')}
            title="Remove whole team from this load and all future loads"
          >
            x Drop team ->
          </button>
          <button
            type="button"
            className={`edm-btn edm-btn--roster${rosterOpen ? ' edm-btn--roster-open' : ''}`}
            onClick={() => setRosterOpen((open) => !open)}
            title="Add or remove people from this slot"
          >
            {rosterOpen ? 'x Close' : '+ / - People'}
          </button>
        </div>
      </div>

      {rosterOpen && (
        <div className="edm-roster-panel">
          <div className="edm-roster-search-row">
            <input
              className="edm-roster-input"
              placeholder="Search name or email..."
              value={rosterSearch}
              onChange={(e) => setRosterSearch(e.target.value)}
              autoFocus
            />
            <select
              className="edm-roster-scope"
              value={rosterScope}
              onChange={(e) => setRosterScope(e.target.value)}
            >
              <option value="this-load">This load</option>
              <option value="round-onward">Round onward</option>
              <option value="all-rounds">All rounds</option>
            </select>
          </div>
          <ul className="edm-roster-list">
            {filteredPeople.map((person) => (
              <li
                key={person.id}
                className={`edm-roster-item${person.inSlot ? ' edm-roster-item--selected' : ''}`}
                onClick={() => togglePerson(person.email, rosterScope)}
              >
                {person.inSlot && <span className="edm-roster-check">✓</span>}
                <span className="edm-roster-item-name">{person.name}</span>
                {person.isMentor && <span className="edm-roster-item-role">mentor</span>}
                {person.isFreshling && <span className="edm-roster-item-role edm-roster-item-role--freshling">freshling</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="edm-members">
        {getSlotParticipantEmailsSorted(slot).map((email, memberIdx) => {
          const emailKey = (email || '').toLowerCase().trim();
          const f = flyersByEmail.get(emailKey);
          const name = f ? getDisplayName(f) || email : email;
          const role = getParticipantRoleForSlot(slot, email);
          const isMentor = role === 'mentor';
          const isFreshling = role === 'freshling';
          const isPersonHighlighted = isSlotHighlighted && highlightedEmailKey === emailKey;
          return (
            <div
              key={`${emailKey}-${memberIdx}`}
              className={`edm-member${isPersonHighlighted ? ' edm-member--search-highlight' : ''}`}
              draggable
              onDragStart={(e) => {
                setDragState({ type: 'participant', email, sourceSlot: slot });
                e.dataTransfer.setData('text/plain', email);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragEnd={() => setDragState(null)}
            >
              {isMentor && <span className="edm-mentor-star" aria-label="Mentor">★</span>}
              {isFreshling && (
                <span className="edm-freshling-mark" aria-label="Freshling">
                  ◇
                </span>
              )}
              <span className="edm-member-name">{name || email || '-'}</span>
              <div className="edm-member-actions">
                <button
                  type="button"
                  className="edm-btn--skip-person"
                  onClick={() => handleRemoveParticipantByScope(slot, email, 'load')}
                  title="Mark absent for this load only; still on future loads"
                >
                  Skip load
                </button>
                <button
                  type="button"
                  className="edm-btn--drop-person"
                  onClick={() => handleRemoveParticipantByScope(slot, email, 'forward')}
                  title="Remove from this load and all future loads"
                >
                  Drop ->
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TeamSlotAdd({ teams, flyersByEmail, onSelect, selectedRound }) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);
  const filtered = useMemo(() => {
    const term = (filter || '').toLowerCase().trim();
    const teamMatchesFilter = (t) => {
      const num = String(t.teamNumber ?? '');
      const mentorEmail = (t.mentor || '').toLowerCase().trim();
      const mentorName = mentorEmail
        ? (getDisplayName(flyersByEmail.get(mentorEmail)) || '').toLowerCase()
        : '';
      if (!term) return true;
      return (
        num.toLowerCase().includes(term) ||
        mentorEmail.includes(term) ||
        mentorName.includes(term)
      );
    };
    return teams.filter(teamMatchesFilter).slice(0, 25);
  }, [teams, filter, flyersByEmail]);
  return (
    <div className="event-day-manifest-team-add-wrap" ref={ref}>
      <button
        type="button"
        className="event-day-manifest-add-btn event-day-manifest-team-add-btn"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        disabled={!teams.length}
        title={
          teams.length
            ? `Place a team in this slot (round ${selectedRound ?? '?'} only)`
            : 'No teams for this event'
        }
      >
        + Team
      </button>
      {open && (
        <div className="event-day-manifest-add-dropdown event-day-manifest-team-add-dropdown">
          <input
            type="text"
            placeholder="Search team # or mentor…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="event-day-manifest-add-input"
            onClick={(e) => e.stopPropagation()}
          />
          <ul className="event-day-manifest-add-list">
            {filtered.map((t) => {
              const mentorEmail = (t.mentor || '').toLowerCase().trim();
              const mentorFlyer = mentorEmail ? flyersByEmail.get(mentorEmail) : null;
              const mentorLabel = mentorFlyer
                ? getDisplayName(mentorFlyer) || t.mentor
                : t.mentor || '—';
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(t);
                      setOpen(false);
                      setFilter('');
                    }}
                  >
                    #{t.teamNumber ?? '?'}
                    {mentorLabel && mentorLabel !== '—' ? ` · ${mentorLabel}` : ''}
                  </button>
                </li>
              );
            })}
          </ul>
          {!filtered.length && (
            <p className="event-day-manifest-team-add-empty">No matching teams.</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Person picker for non-slot contexts (e.g. other jumps). Slot cards use inline roster instead. */
function ParticipantAdd({
  flyers,
  onAdd,
  onRemove,
  inline = false,
  currentEmails = [],
  actionScope = 'load',
  isMentorEmail = () => false,
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const [participantScope, setParticipantScope] = useState(
    actionScope === 'all' ? 'all' : actionScope === 'forward' ? 'forward' : 'load'
  );
  const ref = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);
  const filtered = useMemo(() => {
    const term = (filter || '').toLowerCase().trim();
    const normalizedCurrent = new Set((currentEmails || []).map((e) => (e || '').toLowerCase().trim()));
    const currentEmailsDeduped = [];
    const seenLower = new Set();
    for (const email of currentEmails || []) {
      const key = (email || '').toLowerCase().trim();
      if (!key || seenLower.has(key)) continue;
      seenLower.add(key);
      currentEmailsDeduped.push(email);
    }
    const selected = currentEmailsDeduped
      .map((email) => {
        const key = (email || '').toLowerCase().trim();
        const flyer = flyers.find((f) => (f.email || '').toLowerCase().trim() === key);
        return flyer || { id: key || email, email, name: email };
      })
      .filter((f) => f?.email);
    const unselected = flyers.filter((f) => !normalizedCurrent.has((f.email || '').toLowerCase().trim()));
    const filteredUnselected = (term
      ? unselected.filter((f) => {
          const email = (f.email || '').toLowerCase();
          const name = (getDisplayName(f) || '').toLowerCase();
          return email.includes(term) || name.includes(term);
        })
      : unselected
    ).slice(0, 20);
    const filteredSelected = (term
      ? selected.filter((f) => {
          const email = (f.email || '').toLowerCase();
          const name = (getDisplayName(f) || '').toLowerCase();
          return email.includes(term) || name.includes(term);
        })
      : selected
    ).sort((a, b) => {
      const am = isMentorEmail(a.email) ? 0 : 1;
      const bm = isMentorEmail(b.email) ? 0 : 1;
      if (am !== bm) return am - bm;
      return (getDisplayName(a) || a.email || '').localeCompare(getDisplayName(b) || b.email || '', undefined, {
        sensitivity: 'base',
      });
    });
    return [...filteredSelected, ...filteredUnselected];
  }, [flyers, filter, currentEmails, isMentorEmail]);
  const selectedSet = useMemo(
    () => new Set((currentEmails || []).map((e) => (e || '').toLowerCase().trim())),
    [currentEmails]
  );
  const scopeLabel =
    participantScope === 'all' ? 'all rounds' : participantScope === 'forward' ? 'round onward' : 'this load';
  return (
    <div className="event-day-manifest-add-wrap" ref={ref}>
      {!inline && (
        <button type="button" className="event-day-manifest-add-btn" onClick={() => setOpen((o) => !o)}>
          + Add
        </button>
      )}
      {(inline || open) && (
        <div className={`event-day-manifest-add-dropdown${inline ? ' event-day-manifest-add-dropdown--inline' : ''}`}>
          {inline && <p className="event-day-manifest-add-inline-title">+ Add / remove people</p>}
          <input
            type="text"
            placeholder="Search name or email…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="event-day-manifest-add-input"
          />
          {inline && <p className="event-day-manifest-add-inline-hint">Scope: {scopeLabel}</p>}
          {inline && (
            <label className="event-day-manifest-add-scope-row">
              <span>Scope</span>
              <select value={participantScope} onChange={(e) => setParticipantScope(e.target.value)}>
                <option value="load">This load only</option>
                <option value="forward">Round onward</option>
                <option value="all">All rounds</option>
              </select>
            </label>
          )}
          <ul className="event-day-manifest-add-list">
            {filtered.map((f, listIdx) => {
              const key = (f.email || '').toLowerCase().trim();
              const isSelected = selectedSet.has(key);
              const mentor = isMentorEmail(f.email);
              const rowKey =
                f.id != null && String(f.id).trim() !== ''
                  ? `${String(f.id)}-${listIdx}`
                  : `${key || 'row'}-${listIdx}`;
              return (
                <li key={rowKey}>
                  <button
                    type="button"
                    className={`event-day-manifest-add-list-item${isSelected ? ' is-selected' : ''}`}
                    onClick={() => {
                      if (isSelected && onRemove) onRemove(f.email, participantScope);
                      else onAdd(f.email, participantScope);
                      if (!inline) setOpen(false);
                      setFilter('');
                    }}
                  >
                    <span className="event-day-manifest-add-list-main">
                      {isSelected ? '✓ ' : ''}
                      {getDisplayName(f) || f.email || '—'}
                      {mentor ? '  M' : ''}
                    </span>
                    {f.email && <span className="event-day-manifest-add-list-sub">{f.email}</span>}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default EventDayManifest;
